import * as d3 from 'd3';
import * as d3sankey from 'd3-sankey';

const {registerModule} = window;

class Chart {
  constructor(element) {
    this.element = element;
    this.initialize();
  }

  initialize() {
    this.setVars();
    this.bindEvents();
    this.buildChart();
  }

  setVars() {
    this.matchMediaMobile = window.matchMedia('(max-width: 600px');

    this.chartId = this.element.id || '';
    this.ui = {};
    this.ui.chartContainerArea = this.element.querySelector('.chart--chart-area');
    this.ui.d3Selection = this.element.querySelector('.chart--d3-area');
    this.ui.legendContainer = this.element.querySelector('.chart--legend');

    this.config = {};
    this.config.chartType = this.ui.d3Selection.getAttribute('data-chart-type');
    this.config.xAxisProperties = this.ui.d3Selection
      .getAttribute('data-x-properties')
      .split(',');
    this.config.yAxisProperties = this.ui.d3Selection
      .getAttribute('data-y-properties')
      .split(',');
    this.config.chartPalette = this.ui.d3Selection
      .getAttribute('data-chart-palette')
      .split(',');
    this.config.chartPaletteSecondary = this.ui.d3Selection
      .getAttribute('data-chart-palette-secondary')
      ?.split(',');
    this.config.chartPaletteBackgroundHighlights = this.ui.d3Selection
      .getAttribute('data-chart-palette-background-highlights')
      ?.split(',');
    this.config.chartPaletteTextHighlights = this.ui.d3Selection
      .getAttribute('data-chart-palette-text-highlights')
      ?.split(',');
    this.config.chartLegendTextHighlightColors = this.ui.d3Selection
      .getAttribute('data-chart-legend-text-highlight-colors')
      ?.split(',');
    this.config.chartLegend = this.ui.d3Selection.getAttribute('data-chart-legend')?.length ? this.ui.d3Selection.getAttribute('data-chart-legend')?.split(',') : [];
    this.config.customFloor =
      this.ui.d3Selection.getAttribute('data-custom-floor') || 0;
    this.config.chartPositionSettings = this.ui.d3Selection.getAttribute(
      'data-chart-position-settings',
    ) ?
      JSON.parse(
        this.ui.d3Selection.getAttribute('data-chart-position-settings'),
      ) :
      {};

    this.chartVars = {};
    this.chartVars.margins = {top: 10, right: 50, bottom: 30, left: 40};

    const chartAnimationSettingsDataAttribute = this.ui.d3Selection.getAttribute('data-chart-animation-settings');

    if (chartAnimationSettingsDataAttribute) {
      let chartAnimationSettings;
      try {
        chartAnimationSettings = JSON.parse(chartAnimationSettingsDataAttribute).animationSettings;
      } catch {
        console.error('Invalid animation settings JSON');
      }

      // Set animation settings from Contentful if they are provided
      this.animationSettings = {...chartAnimationSettings};
    } else {
      // If no Contentful animation settings are provided, provide a default
      this.animationSettings = {
        // Delay before starting animations (useful for debugging)
        animationStartDelay: 0,
        // For bar charts: Time delay between each bar's animation
        animationSecondaryDelay: 0,
        animationStagger: 400,
        // For bar charts: Duration of bar height animation
        animationDuration: 500,
        // For bar charts: Duration of bar animation while hovered
        animationHoverDuration: 500,
        // While the bars grow hovering over them creates a new transition causing the bar growth animation to be interrupted. Using transition names allows the animations to proceed simultaneously.
        animationHoverTransitionName: 'animation-hover',
        animationGrowTransitionName: 'animation-grow',
        // Index added to the data for animations
        animationIndexProperty: '_index',
        animationInViewThreshold: 0.5,
        // Settings for Likert horizontal bar chart, specifically
        likertBarChartHorizontalBarsPercentageAnimationDuration: 0,
        likertBarChartHorizontalBarsPercentageAnimationDelay: 0,
      };
    }

    this.data = JSON.parse(this.ui.d3Selection.getAttribute('data-chart-data')).data.map((item, index) => {
      // Assign an index to each item of the data for use in transitions
      if (this.animationSettings.animationIndexProperty in item === false) {
        item[this.animationSettings.animationIndexProperty] = index;
      } else {
        throw new Error(`${this.animationSettings.animationIndexProperty} property cannot be used in the data`);
      }
      return item;
    });

    this.options = {};
    if (JSON.parse(this.ui.d3Selection.getAttribute('data-chart-data')).options) {
      this.options = JSON.parse(this.ui.d3Selection.getAttribute('data-chart-data')).options;
    }
  }

  isElementInViewOnce(element, callback, inViewThreshold) {
    // Executes the callback function once when the element is in view. Additional calls to the function after the callback has executed will be ignored. Useful for setting up listeners or animations in the chart.
    let elementViewed = false;
    const handleIntersection = (entries) => {
      entries.map((entry) => {
        if (entry.isIntersecting && !elementViewed) {
          callback();
          elementViewed = true;
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {threshold: inViewThreshold});

    observer.observe(element);
  }

  bindEvents() {
    this.matchMediaMobile.addEventListener('change', () => {
      this.buildChart();
    });
  }

  buildChart() {
    this.chartVars.height = 544;
    this.chartVars.width = 544; // in practice this will be drawn from the container div; can't put this in bindUI without refactoring further
    let xSet;
    let fxSet;
    let ySet;
    let fySet;
    let filteredData;

    if (this.config.xAxisProperties && this.config.xAxisProperties.length > 1) {
      this.chartVars.xGroupProp = this.config.xAxisProperties[0];
      this.chartVars.xProp = this.config.xAxisProperties[1];
    } else if (
      this.config.xAxisProperties &&
      this.config.xAxisProperties.length === 1
    ) {
      this.chartVars.xProp = this.config.xAxisProperties[0];
    }

    if (this.config.yAxisProperties && this.config.yAxisProperties.length > 1) {
      this.chartVars.yGroupProp = this.config.yAxisProperties[0];
      this.chartVars.yProp = this.config.yAxisProperties[1];
    } else if (
      this.config.yAxisProperties &&
      this.config.yAxisProperties.length === 1
    ) {
      this.chartVars.yProp = this.config.yAxisProperties[0];
    }

    if (!this.chartVars.xProp) return;

    if (this.ui.legendContainer && this.config.chartLegend?.length) {
      const legendItemsString = this.config.chartLegend.map(
        (legendLabel, i) => {
          return `<li class="chart--legend-item" data-likert-bar-chart-horizontal-bars-category="${legendLabel}" data-bar-index="${i}">
            <div class="chart--legend-item-color" style="background-color:${this.config.chartPalette[i]}" data-likert-bar-chart-horizontal-bars-category="${legendLabel}" data-bar-index="${i}" data-legend-color="${this.config.chartPalette[i]}" data-legend-highlight-color="${this.config.chartPaletteBackgroundHighlights[i]}">  </div>
            <div class="chart--legend-item-label" data-likert-bar-chart-horizontal-bars-category="${legendLabel}" data-bar-index="${i}" data-legend-text-highlight-color="${this.config.chartLegendTextHighlightColors[i]}" data-legend-text-color="#000">${legendLabel}</div>
          </li>`;
        },
      );

      this.ui.legendContainer.innerHTML =
        '<ul class="chart--legend-items">' +
        legendItemsString.join('') +
        '</ul>';
    }

    // configurations based on chart types
    switch (this.config.chartType) {
    case 'Sankey Chart': {// sk1
      if (this.matchMediaMobile.matches) {
        this.chartVars.width = this.options?.widthOverride || 450;
      }
      this.chartVars.margins = {top: 0, right: 10, bottom: 10, left: 10};
      this.nodeData = JSON.parse(this.ui.d3Selection.getAttribute('data-chart-data')).nodes;
      this.chartVars.customAxis = this.nodeData.map((node) => {
        const splitLabel = node[this.chartVars.xGroupProp].split('<br>');
        return splitLabel;
      });
      this.chartVars.axisRows = Math.max(...this.chartVars.customAxis.map(((axis) => axis.length))) + 1;
      this.chartVars.margins.top = this.getSankeyPadding(this.chartVars.axisRows);
      const sankey = d3sankey.sankey()
        .nodeId((d) => d.category)
        .nodeWidth(40)
        .nodePadding(this.getSankeyPadding(this.chartVars.axisRows) + 10)
        .extent([[this.chartVars.margins.left, this.chartVars.margins.top], [this.chartVars.width - this.chartVars.margins.right, this.chartVars.height - this.chartVars.margins.bottom]]);
      this.sankey = sankey({
        nodes: this.nodeData.map((d) => Object.assign({}, d)),
        links: this.data.map((d) => Object.assign({}, d)),
      });
      xSet = new Set(this.sankey.nodes.map((d) => d[this.chartVars.xProp]));
      this.sankey.nodes.forEach((node, idx) => this.chartVars.customAxis[idx].push(`${node[this.chartVars.xProp]}`));
      this.chartVars.palettes = this.getPalettes(xSet);
      break;}
    case 'Donut Chart': // dc1
      if (this.matchMediaMobile.matches) {
        this.chartVars.width = this.options?.widthOverride || 450;
      }
      this.chartVars.height = this.chartVars.width;
      this.chartVars.margins = {
        top: 0,
        right: 100,
        bottom: 0,
        left: 100,
      };
      this.chartVars.radius =
          Math.min(
            this.chartVars.width -
              this.chartVars.margins.right -
              this.chartVars.margins.left,
            this.chartVars.height -
              this.chartVars.margins.top -
              this.chartVars.margins.bottom,
          ) / 2;

      this.chartVars.arc = d3
        .arc()
        .innerRadius((this.chartVars.radius / 2) * 1.7 - 6)
        .outerRadius(this.chartVars.radius);
      this.chartVars.pie = d3
        .pie()
        .sort(null)
        .value((d) => {
          return d[this.chartVars.xGroupProp];
        });

      this.chartVars.palettes = this.getPalettes(new Set(this.data.map((d) => d[this.chartVars.xProp])));

      if (this.chartVars.yProp) {
        const outerData = [];
        const innerData = [];
        const dupeIndices = {};
        this.data.forEach((datum, i) => {
          const dupeKey = `${datum[this.chartVars.xProp]}--${datum[this.chartVars.xGroupProp]}`;
          if (dupeIndices[dupeKey]) {
            datum.dupe = true;
            this.data[dupeIndices[dupeKey]].dupe = true;
          } else {
            dupeIndices[dupeKey] = i;
          }

          if (datum[this.chartVars.yProp] === 'inner') {
            innerData.push(datum);
          } else {
            outerData.push(datum);
          }
        });
        this.innerData = innerData;
        this.data = outerData;

        this.chartVars.innerPie = d3
          .pie()
          .sort(null)
          .value((d) => {
            return d[this.chartVars.xGroupProp];
          });
        this.chartVars.innerArc = d3
          .arc()
          .innerRadius(0)
          .outerRadius((d) => {
            if (d.data.dupe) {
              return this.chartVars.radius;
            }
            return (this.chartVars.radius / 2) * 1.7 - 6;
          });
      }

      this.chartVars.customAxis = this.getDonutCustomAxis(this.data);
      if (this.innerData) {
        this.chartVars.customAxisInner = this.getDonutCustomAxis(this.innerData);
      }

      break;
    case 'Line Chart': // lc1
      this.chartVars.width = 600;
      if (this.matchMediaMobile.matches) {
        this.chartVars.width = this.options?.widthOverride || 450;
      }
      this.chartVars.height = this.options?.height || 300;
      this.chartVars.margins.top = 20;
      this.chartVars.margins.right = 0;
      if (this.options.yAxis.title) {
        this.chartVars.margins.left = 70;
      }
      if (this.options.xAxis.title) {
        this.chartVars.margins.bottom = 65;
      }
      this.chartVars.axisType = this.determineAxisType(this.data[0], this.chartVars.xProp);
      xSet = [...new Set(this.data.map((d) => d[this.chartVars.xProp]))];
      if (this.options.secondaryData) {
        xSet = [...xSet, ...new Set(this.options.secondaryData.map((d) => d[this.chartVars.xProp]))];
      }
      xSet.forEach((string) => this.chartVars.axisType === 'date' ? this.parseDate(string) : this.parseNumber(string));
      this.chartVars.ticks = {
        x: this.options?.xAxis?.ticks || [],
        y: this.options?.yAxis?.ticks || [],
      };
      this.chartVars.bounds = {
        x: this.options?.xAxis?.bounds || [],
        y: this.options?.yAxis?.bounds || [],
      };

      fySet = new Set(this.data.map((d) => d[this.chartVars.yGroupProp]));
      this.chartVars.palettes = this.getPalettes(fySet);
      this.chartVars.x = this.chartVars.axisType === 'date' ?
        d3.scaleTime().domain([
          d3.min(this.chartVars.bounds.x.map((string) => this.parseDate(string))),

          // this.chartVars.ticks[0],
          d3.max(this.chartVars.bounds.x.map((string) => this.parseDate(string))),
          // this.chartVars.ticks[this.chartVars.ticks.length -1],
        ])
          .range([
            this.chartVars.margins.right,
            this.chartVars.width - this.chartVars.margins.left,
          ]) :
        d3
          .scaleLinear()
          .domain([
            d3.min(this.chartVars.bounds.x),
            d3.max(this.chartVars.bounds.x),
            // this.chartVars.ticks[0],
            // Math.max(this.chartVars.ticks[this.chartVars.ticks.length -1], 13),
          ])
          .range([
            this.chartVars.margins.right,
            this.chartVars.width - this.chartVars.margins.left,
          ]);
      this.chartVars.y = d3
        .scaleLinear()
        .domain([
          d3.min(this.chartVars.bounds.y),
          d3.max(this.chartVars.bounds.y),
          // d3.max(this.data, (d) => this.parseNumber(d[this.chartVars.yProp])),
        ])
        .rangeRound([
          this.chartVars.height - this.chartVars.margins.bottom,
          this.chartVars.margins.top,
        ]);

      this.series = {};
      this.data.forEach((datum) => {
        if (datum[this.chartVars.yProp]) {
          if (!this.series[datum[this.chartVars.yGroupProp]]) {
            this.series[datum[this.chartVars.yGroupProp]] = [];
          }
          this.series[datum[this.chartVars.yGroupProp]].push(datum);
        }
      });
      if (this.options.secondaryData) {
        this.secondarySeries = {};
        this.options.secondaryData.forEach((datum) => {
          if (datum[this.chartVars.yProp]) {
            if (!this.secondarySeries[datum[this.chartVars.yGroupProp]]) {
              this.secondarySeries[datum[this.chartVars.yGroupProp]] = [];
            }
            this.secondarySeries[datum[this.chartVars.yGroupProp]].push(datum);
          }
        });
      }
      if (this.options.curveData) {
        this.curveSeries = {};
        this.options.curveData.forEach((datum) => {
          if (datum[this.chartVars.yProp]) {
            if (!this.curveSeries[datum[this.chartVars.yGroupProp]]) {
              this.curveSeries[datum[this.chartVars.yGroupProp]] = [];
            }
            this.curveSeries[datum[this.chartVars.yGroupProp]].push(datum);
          }
        });
      }

      this.chartVars.line = d3.line()
        .x((d) => {
          const val = this.chartVars.axisType === 'date' ? this.parseDate(d[this.chartVars.xProp]) : this.parseNumber(d[this.chartVars.xProp]);
          return this.chartVars.x(val);
          // return this.chartVars.x(this.parseDate(d[this.chartVars.xProp]));
        })
        .y((d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])));

      this.chartVars.curve = d3.line()
        .x((d) => {
          const val = this.chartVars.axisType === 'date' ? this.parseDate(d[this.chartVars.xProp]) : this.parseNumber(d[this.chartVars.xProp]);
          return this.chartVars.x(val);
          // return this.chartVars.x(this.parseDate(d[this.chartVars.xProp]));
        })
        .y((d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp]))).curve(d3.curveBasis);

      break;


    case 'Bar Chart - Vertical Bars': // vb1
      // on mobile, smaller horizontal margins
      if (this.matchMediaMobile.matches) {
        this.chartVars.width = this.options?.widthOverride || 450;
        this.chartVars.margins = {
          top: 40,
          right: 0,
          bottom: 16,
          left: 0,
        };
      } else {
        this.chartVars.margins = {
          top: 40,
          right: 12,
          bottom: 16,
          left: 12,
        };
      }
      this.chartVars.height = 400;

      xSet = new Set(this.data.map((d) => d[this.chartVars.xProp]));
      this.chartVars.customAxis = [...xSet].map((label) => label.split('<br>'));
      this.chartVars.axisRows = Math.max(...this.chartVars.customAxis.map(((axis) => axis.length)));
      if (this.chartVars.axisRows > 0) {
        this.chartVars.margins.bottom = this.chartVars.margins.bottom + (this.chartVars.axisRows * 22) + 16;
      }
      this.chartVars.x = d3
        .scaleBand()
        .domain(xSet)
        .rangeRound([
          this.chartVars.margins.left,
          this.chartVars.width - this.chartVars.margins.right,
        ]);

      this.chartVars.palettes = this.getPalettes(xSet);

      this.chartVars.styleMap = this.getStyleMap(this.data, this.chartVars.yProp);

      this.chartVars.y = d3
        .scaleLinear()
        .domain([
          this.config.customFloor,
          d3.max(this.data, (d) => this.parseNumber(d[this.chartVars.yProp])),
        ])
        .nice()
        .rangeRound([
          this.chartVars.height - this.chartVars.margins.bottom,
          this.chartVars.margins.top,
        ]);
      break;

    case 'Bar Chart - Horizontal Bars': // hb1
      this.chartVars.height = this.data.length * 57;
      if (this.matchMediaMobile.matches) {
        this.chartVars.width = this.options?.widthOverride || 450;
      }
      this.chartVars.margins = {top: 0, right: 40, bottom: 0, left: 0};

      ySet = new Set(this.data.map((d) => d[this.chartVars.yProp]));
      this.chartVars.y = d3
        .scaleBand()
        .domain(ySet)
        .rangeRound([
          this.chartVars.margins.top,
          this.chartVars.height - this.chartVars.margins.bottom,
        ]);
      this.chartVars.palettes = this.getPalettes(ySet);

      this.chartVars.styleMap = this.getStyleMap(this.data, this.chartVars.xProp);


      this.chartVars.x = d3
        .scaleLinear()
        .domain([0, d3.max(this.data, (d) => this.parseNumber(d[this.chartVars.xProp]))])
        .nice()
        .rangeRound([
          this.chartVars.width - this.chartVars.margins.left,
          this.chartVars.margins.right,
        ]);
      break;

    case 'Bar Chart - Zig Zags': // zz1
      this.chartVars.margins = {
        top: 21,
        right: 0,
        bottom: 0,
        left: 50,
      };
      ySet = new Set(this.data.map((d) => d[this.chartVars.yProp])); // 'year'
      fxSet = new Set(this.data.map((d) => d[this.chartVars.xGroupProp])); // 'type'

      this.series = {};
      this.lineSeries = {};
      this.xOffsets = {};
      this.data.forEach((datum) => {
        let lineFinesse = 0;
        if (datum[this.chartVars.yProp]) {
          if (!this.series[datum[this.chartVars.yProp]]) {
            this.series[datum[this.chartVars.yProp]] = [];
          }
          this.series[datum[this.chartVars.yProp]].push(datum);

          if (!this.xOffsets[datum[this.chartVars.yProp]]) {
            this.xOffsets[datum[this.chartVars.yProp]] = 0;
          }
          this.xOffsets[datum[this.chartVars.yProp]] += this.parseNumber(datum[this.chartVars.xProp]);

          if (this.options?.lineFinesse) {
            lineFinesse = this.options.lineFinesse[datum._index % fxSet.size][Math.floor(datum._index / fxSet.size)];
          }
        }

        if (datum[this.chartVars.xGroupProp]) {
          if (!this.lineSeries[datum[this.chartVars.xGroupProp]]) {
            this.lineSeries[datum[this.chartVars.xGroupProp]] = [];
          }
          this.lineSeries[datum[this.chartVars.xGroupProp]].push({...datum, yOffset: 21, xOffset: this.xOffsets[datum[this.chartVars.yProp]], lineFinesse});
          this.lineSeries[datum[this.chartVars.xGroupProp]].push({...datum, yOffset: 67, xOffset: this.xOffsets[datum[this.chartVars.yProp]], lineFinesse});
        }
      });

      this.maxLength = 0;
      this.maxTotal = 0;
      this.series = Object.entries(this.series).map(([key, val]) => {
        this.maxLength = Math.max(this.maxLength, val.length);
        let total = 0;
        val.forEach((datum) => {
          datum.start = total;
          total += this.parseNumber(datum[this.chartVars.xProp]);
        });
        this.maxTotal = Math.max(this.maxTotal, total);
        val.key = key;
        return val;
      });
      this.lineSeries = Object.entries(this.lineSeries).map(([key, val], i) => {
        val.key = key;
        val[0].yOffset = 26;
        val[val.length - 1].yOffset = 62;
        val[this.animationSettings.animationIndexProperty] = i;
        return val;
      });

      this.chartVars.height = ySet.size * 88 + 21;
      this.chartVars.margins.right = this.maxLength * 11 - 3;

      this.chartVars.y = d3
        .scaleBand()
        .domain(ySet)
        .rangeRound([
          this.chartVars.margins.top,
          this.chartVars.height - this.chartVars.margins.bottom,
        ]);

      this.chartVars.palettes = this.getPalettes(fxSet);

      this.chartVars.x = d3
        .scaleLinear()
        .domain([0, this.maxTotal])
        .nice()
        .rangeRound([
          0,
          this.chartVars.width - this.chartVars.margins.left - this.chartVars.margins.right,
        ]);

      this.chartVars.line = d3.line()
        .x((d) => {
          return this.chartVars.x(this.parseNumber(d.xOffset)) + this.chartVars.margins.left + d.lineFinesse;
        })
        .y((d) => {
          return this.chartVars.y(d[this.chartVars.yProp]) + d.yOffset;
        });

      break;

    case 'Bar Chart - Horizontal Groups': // gh1

      if (this.matchMediaMobile.matches) {
        this.chartVars.width = this.options?.widthOverride || 450;
      }
      this.chartVars.margins = {top: 0, right: 50, bottom: 0, left: 0};


      fySet = new Set(this.data.map((d) => d[this.chartVars.yGroupProp]));
      ySet = new Set(this.data.map((d) => d[this.chartVars.yProp]));

      /*
      / Regarding the height: 40 is 32 (between groups) plus 16 (space between group title and first bar) minus 8
      / (space between bars, to account for the fact that the final bar in a group is missing the corresponding 8
      / pixel gap), plus 22 (for the group titles). 28 is the height of the individual bars plus the gap
      / following each bar.
      */
      this.chartVars.height = fySet.size * (62 + ySet.size * 28);

      this.chartVars.fy = d3
        .scaleBand()
        .domain(fySet)
        .rangeRound([
          this.chartVars.margins.top,
          this.chartVars.height - this.chartVars.margins.bottom,
        ]);

      this.chartVars.y = d3
        .scaleBand()
        .domain(ySet)
        .rangeRound([38, this.chartVars.fy.bandwidth() - 32]);

      this.chartVars.palettes = this.getPalettes(ySet);

      this.chartVars.styleMap = this.getStyleMap(this.data, this.chartVars.xProp);

      this.chartVars.x = d3
        .scaleLinear()
        .domain([0, d3.max(this.data, (d) => this.parseNumber(d[this.chartVars.xProp]))])
        .nice()
        .rangeRound([
          this.chartVars.margins.left,
          this.chartVars.width + this.chartVars.margins.left - this.chartVars.margins.right,
        ]);

      this.series = {};
      this.data.forEach((datum) => {
        if (datum[this.chartVars.yProp]) {
          if (!this.series[datum[this.chartVars.yGroupProp]]) {
            this.series[datum[this.chartVars.yGroupProp]] = [];
          }
          this.series[datum[this.chartVars.yGroupProp]].push(datum);
        }
      });

      this.series = Object.entries(this.series).map(([key, val]) => {
        val.key = key;
        return val;
      });

      break;

    case 'Likert Bar Chart - Normalized, Horizontal': // lb1
      if (!this.chartVars.xGroupProp || !this.chartVars.yGroupProp) return;

      // on mobile, first percentages should float to left of bars when first percentage is tiny
      if (this.matchMediaMobile.matches) {
        this.chartVars.width = this.options?.widthOverride || 400;
        this.chartVars.margins = {
          top: 10,
          right: 25,
          bottom: 80,
          left: 25,
        };
      } else {
        this.chartVars.margins = {top: 10, right: 40, bottom: 80, left: 0};
      }

      this.chartVars.spacers = {
        spaceBetweenGroups: 15,
        spaceBetweenBars: 10,
        groupHeight: 45,
      };

      this.chartVars.poppedPercent = 5;

      // get array
      this.yGroupPropKeys = [
        ...d3.union(this.data.map((d) => d[this.chartVars.yGroupProp])),
      ];

      xSet = new Set(this.data.map((d) => d[this.chartVars.xGroupProp]));

      this.chartVars.palettes = this.getPalettes(xSet);

      this.chartVars.indents = {left: false};
      this.series = this.yGroupPropKeys.map((prop, seriesIndex) => {
        filteredData = this.data.filter((d) => {
          return d[this.chartVars.yGroupProp] === prop;
        });
        const singleSeries = d3
          .stack()
          .keys(
            d3.union(
              filteredData.map((d) => {
                return d[this.chartVars.yProp]; // was fx
              }),
            ),
          ) // distinct series keys, in input order
          .value(([, D], key) => {
            return D.get(key)[this.chartVars.xProp];
          }) // get value for each series key and stack
          .offset(d3.stackOffsetNone)(
            d3.index(
              filteredData,
              (d) => {
                return d[this.chartVars.xGroupProp]; // was y
              },
              (d) => {
                return d[this.chartVars.yProp]; // was fx
              },
            ),
          ); // group by stack then series key
        singleSeries['key'] = prop;
        const totals = new Array(singleSeries.length).fill(0);
        singleSeries.forEach((levelOne, indexOne) => {
          let upper = 0;
          let lower = 0;
          levelOne.forEach((levelTwo) => {
            totals[indexOne] += levelTwo[1];
            totals[indexOne] -= levelTwo[0];
            levelTwo['x'] = upper - lower;
            upper += levelTwo[1];
            lower += levelTwo[0];
            levelTwo[this.animationSettings.animationIndexProperty] = seriesIndex * 2 + indexOne;
          });
          singleSeries.forEach((levelOne, indexOne) => {
            if (levelOne[0][1] - levelOne[0][0] <= this.chartVars.poppedPercent) {
              this.chartVars.indents.left = true;
            }
            levelOne.forEach((levelTwo) => {
              levelTwo['total'] = totals[indexOne];
            });
          });
        });
        return singleSeries;
      });
      if (!this.chartVars.indents.left) {
        this.chartVars.margins.left = 0;
      }

      // series.length is the number of groups and series[0].length is the number of stacked bars in each group

      this.chartVars.height =
        this.series.length * (this.series[0].length * this.chartVars.spacers.groupHeight + this.chartVars.spacers.spaceBetweenBars) + this.chartVars.spacers.spaceBetweenGroups + this.chartVars.margins.top + this.chartVars.margins.bottom;
      // this.series.length * (this.series[0].length * 55 - 8 + 52) - 16;

      this.chartVars.fy = d3
        .scaleBand()
        .domain(
          new Set(
            this.data.map((d) => {
              return d[this.chartVars.yGroupProp];
            }),
          ),
        )
        .rangeRound([
          this.chartVars.margins.top,
          this.chartVars.height - this.chartVars.margins.bottom,
        ]);

      this.chartVars.y = d3
        .scaleBand()
        .domain(new Set(this.data.map((d) => d[this.chartVars.yProp])))
        .rangeRound([0, this.chartVars.fy.bandwidth()]);
      // .paddingInner(0.3);

      this.chartVars.x = d3
        .scaleLinear()
        .domain([0, 1])
        .range([
          this.chartVars.margins.left,
          this.chartVars.width - this.chartVars.margins.right,
        ]);

      break;
    default:
      return;
    }

    this.svg = d3
      .create('svg')
      .attr('width', this.chartVars.width)
      .attr('height', this.chartVars.height)
      .attr('viewBox', [0, 0, this.chartVars.width, this.chartVars.height])
      .attr('style', 'max-width: 100%; height: auto; width: 100%');

    this.ui.d3Selection.replaceChildren();
    this.ui.d3Selection.append(this.svg.node());

    switch (this.config.chartType) {
    case 'Sankey Chart':
      this.buildSankeyChart();
      break;
    case 'Donut Chart':
      this.buildDonutChart();
      break;
    case 'Line Chart':
      this.buildLineChart();
      break;
    case 'Bar Chart - Vertical Bars':
      this.buildBarChartVerticalBars();
      break;
    case 'Bar Chart - Horizontal Bars':
      this.buildBarChartHorizontalBars();
      break;
    case 'Bar Chart - Zig Zags':
      this.buildBarChartZigZags();
      break;
    case 'Bar Chart - Horizontal Groups':
      this.buildBarChartGroupedHorizontalBars();
      break;
    case 'Likert Bar Chart - Normalized, Horizontal':
      this.buildLikertBarChartHorizontalBars();
      break;
    default:
      return;
    }
  }

  buildSankeyChart() { // sk2
    const thisScope = this;

    // gradient definitions
    const defs = this.svg.append('defs');

    // node rectangles
    const rects = this.svg.append('g')
      .selectAll()
      .data(this.sankey.nodes)
      .join('rect')
      .attr('x', (d) => d.x0)
      .attr('y', (d) => d.y0)
      .attr('data-sankey-node', '')
      .attr('data-sankey-source', (d) => d.sourceLinks.length ? d.category : '')
      .attr('data-sankey-target', (d) => d.targetLinks.length ? d.category : '')
      .attr('height', (d) => d.y1 - d.y0)
      .attr('width', (d) => d.x1 - d.x0)
      .attr('fill', (d) => {
        return this.chartVars.palettes.paletteOne(d[this.chartVars.xProp]);
      });

    // link paths
    const links = this.svg.append('g')
      .attr('fill', 'none')
      .selectAll()
      .data(this.sankey.links)
      .join('g')
      .attr('data-sankey-link', '')
      // .attr('data-sankey-index', ((_, idx) => idx))
      .attr('data-sankey-source', (d) => d.source.category)
      // .attr('data-sankey-source-value', (d) => d.source.value)
      .attr('data-sankey-target', (d) => d.target.category)
      // .attr('data-sankey-target-value', (d) => d.target.value)
      .style('mix-blend-mode', 'multiply');

    this.sankey.links.forEach((link, idx) => {
      const gradient = defs.append('linearGradient')
        .attr('id', `gradient-${this.chartId}-${idx}`)
        .attr('gradientUnits', 'userSpaceOnUse');
      gradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', (() => this.chartVars.palettes.paletteOne(link.source.value)));
      gradient
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', (() => this.chartVars.palettes.paletteOne(link.target.value)));
    });

    links.append('path')
      .attr('d', d3sankey.sankeyLinkHorizontal())
      .attr('stroke-opacity', 0.5)
      .attr('stroke', (d) => {
        return this.chartVars.palettes.paletteOne(d[this.chartVars.fxProp]);
      })
      .attr('stroke-width', (d) => Math.max(1, d.width));

    links.append('path')
      .attr('d', d3sankey.sankeyLinkHorizontal())
      .attr('stroke-opacity', (d) => d.source.opacity || d.target.opacity || 0.5)
      .attr('stroke', (_, idx) => `url('#gradient-${this.chartId}-${idx}')`)
      .attr('opacity', 0)
      .attr('stroke-width', (d) => Math.max(1, d.width));

    // Adds labels on the links.
    this.svg.append('g')
      .selectAll()
      .data(this.sankey.links)
      .join('text')
      .attr('x', (d) => d.source.x1 + 12)
      .attr('y', (d) => d.y0 + 4)
      .attr('text-anchor', 'start')
      .attr('class', 'chart--text-xs')
      .style('pointer-events', 'none')
      .text((d) => {
        return `${d.value}%`;
      });

    // Add labels on the nodes
    this.svg.append('g')
      .selectAll()
      .data(this.chartVars.customAxis)
      .join('g')
      .attr('transform', (d, idx) => {
        return `translate(${
          this.sankey.nodes[idx].x0 < this.chartVars.width / 2 ? this.sankey.nodes[idx].x0 : this.sankey.nodes[idx].x1
        },${
          // may need to adjust further
          this.sankey.nodes[idx].y0
        })`;
      })
      .attr('text-anchor', (d, idx) => {
        return this.sankey.nodes[idx].x0 < this.chartVars.width / 2 ? 'start' : 'end';
      })
      .selectAll()
      .data((d) => {
        return d;
      })
      .join('text')
      .attr('class', (d, idx, group) => group.length -1 > idx ? 'chart--text-xs f-sans-bold' : 'chart--text-xs')
      .attr('y', (d, idx, group) => {
        let offset = -12;
        let offsetMult = group.length - idx - 1;
        if (offsetMult > 0) {
          offset -= 16;
          offsetMult -= 1;
        }
        offset -= (offsetMult * 12);
        return offset;
      })
      .text((d, idx, group) => group.length -1 > idx ? d : `${d}%`);

    this.isElementInViewOnce(this.ui.d3Selection, () => {
      rects
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          // Animation stagger is determined by looking up the this.animationSettings.animationIndexProperty on the data. For example, in a horizontal bar chart the leftmost bar is has an index of 0, following that 1, 2, etc.
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } );

      links
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } );

      [rects].forEach((rect) => rect.on('mouseover', function(event) {
        const hoverSourceCategory = event.target.getAttribute('data-sankey-source');
        const hoverTargetCategory = event.target.getAttribute('data-sankey-target');


        if (hoverSourceCategory) {
          const allSourceLinks = `[data-sankey-link][data-sankey-source="${hoverSourceCategory}"] > path:nth-child(2)`;
          d3
            .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
            .selectAll(allSourceLinks)
            .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('opacity', 1);
        }

        if (hoverTargetCategory) {
          const allTargetLinks = `[data-sankey-link][data-sankey-target="${hoverTargetCategory}"] > path:nth-child(2)`;
          d3
            .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
            .selectAll(allTargetLinks)
            .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('opacity', 1);
        }
      })
        .on('mouseout', function() {
          const allLinks = '[data-sankey-link] > path:nth-child(2)';
          d3
            .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
            .selectAll(allLinks)
            .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('opacity', 0);
        }));

      [links].forEach((link) => link.on('mouseover', function(event) {
        d3.select(event.target)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('opacity', 1);
      }).on('mouseout', function(event) {
        d3.select(event.target)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('opacity', 0);
      }),
      );
    });
  }

  buildDonutChart() { // dc2
    const thisScope = this;
    const dataPie = this.chartVars.pie(this.data);

    const minX =
      this.config.chartPositionSettings?.viewbox?.minX ||
      -(
        this.chartVars.width
      ) / 2;
    const minY =
      this.config.chartPositionSettings?.viewbox?.minY ||
      -(
        this.chartVars.height
      ) / 2;

    this.svg.attr('viewBox', [minX, minY, this.chartVars.width, this.chartVars.height]); // offsets the fact that pie's point of origin is center
    const arcs = this.svg
      .append('g')
      .selectAll()
      .data(dataPie)
      .join('path')
      .attr('fill', (d) => this.chartVars.palettes.paletteOne(d.data[this.chartVars.xProp]))
      .attr('style', (d) => `rotate: ${(-180 * d.startAngle) / Math.PI}deg`)
      .attr('d', (d) => this.chartVars.arc(d))
      .attr('stroke-width', 6)
      .attr('class', 'chart--transparent-stroke')
      .attr('opacity', 0);

    // arc labels
    const labels = this.svg
      .append('g')
      .selectAll()
      .data(this.chartVars.customAxis)
      .join('g')
      .attr('data-donut-label', '')
      .attr('text-anchor', (d, idx) => {
        const medianRadian =
          dataPie[idx].startAngle / 2 + dataPie[idx].endAngle / 2;
        if (medianRadian <= Math.PI) {
          return 'start';
        } else if (( medianRadian >= 1.95 * Math.PI) && ( dataPie[0].endAngle > 0.33 ) ) {
          return 'start';
        } else {
          return 'end';
        }
      })
      .attr('transform', (d, idx) => {
        const medianRadian = dataPie[idx].startAngle / 2 + dataPie[idx].endAngle / 2;

        const coords = this.chartVars.arc
          .centroid(dataPie[idx])
          .map((vector) => vector * 1.2);
        if (medianRadian <= 0.5 * Math.PI || medianRadian >= 1.5 * Math.PI) {
          coords[1] = coords[1] - 14 * this.chartVars.customAxis[idx].length - 12;
        } else {
          coords[1] = coords[1] + 4;
        }
        if (dataPie[idx]?.data?.xOffset) {
          coords[0] = coords[0] + dataPie[idx].data.xOffset;
        }
        if (dataPie[idx]?.data?.yOffset) {
          coords[1] = coords[1] + dataPie[idx].data.yOffset;
        }
        return `translate(${coords})`;
      })
      .selectAll()
      .data((d) => d)
      .join('text')
      .attr('opacity', 0)
      .attr('y', (_, i) => 14 * i + 4)
      .text((d) => this.config.chartLegend?.length > 0 || d.dupe ? '' : d.text)
      .attr('class', 'chart--text-xs');

    // percentages
    const percentages = this.svg
      .append('g')
      .selectAll()
      .data(dataPie)
      .join('text')
      .attr('transform', (d, i) => {
        const medianRadian = d.startAngle / 2 + d.endAngle / 2;
        const coords = this.chartVars.arc.centroid(d).map((vector) => vector * 1.2);
        if (medianRadian >= 0.5 * Math.PI && medianRadian <= 1.5 * Math.PI && !this.config.chartLegend?.length) {
          coords[1] = coords[1] + 14 * this.chartVars.customAxis[i].length;
        }
        if (d?.data?.xOffset) {
          coords[0] = coords[0] + d.data.xOffset;
        }
        if (d?.data?.yOffset) {
          coords[1] = coords[1] + d.data.yOffset;
        }

        return `translate(${coords})`;
      })
      .text((d) => d.data.dupe ? '' : `${d.data[this.chartVars.xGroupProp]}%`)
      .attr('opacity', 0)
      .attr('fill', (d) => this.chartVars.palettes.paletteTwo(d.data[this.chartVars.xProp]))
      .attr('dominant-baseline', (d) => {
        const medianRadian = d.startAngle / 2 + d.endAngle / 2;
        if (medianRadian <= 0.5 * Math.PI || medianRadian >= 1.5 * Math.PI) {
          return 'baseline';
        } else {
          return 'hanging';
        }
      })
      .attr('text-anchor', (d) => {
        const medianRadian = d.startAngle / 2 + d.endAngle / 2;
        if (medianRadian <= Math.PI) {
          return 'start';
        } else if (( medianRadian >= 1.95 * Math.PI) && ( dataPie[0].endAngle > 0.33 ) ) {
          return 'start';
        } else {
          return 'end';
        }
      })
      .attr('class', 'chart--text-m')
      .style('font-weight', '600');

    let innerArcs; let innerLabels; let innerPercentages;
    if (this.innerData) {
      const dataPieInner = this.chartVars.innerPie(this.innerData);

      let outerIndex = 0;
      this.ringMap = {};
      this.ringMap.inner = dataPieInner.map((d) => {
        const outerArcs = [];
        while (dataPie[outerIndex] && dataPie[outerIndex].endAngle <= (d.endAngle * 1.01)) {
          outerArcs.push(outerIndex);
          outerIndex++;
        }
        return outerArcs;
      });

      this.ringMap.outer = [];
      this.ringMap.inner.forEach((d, i) => {
        d.forEach(() => this.ringMap.outer.push(i));
      });

      innerArcs = this.svg
        .append('g')
        .selectAll()
        .data(dataPieInner)
        .join('path')
        .attr('fill', (d) => this.chartVars.palettes.paletteOne(d.data[this.chartVars.xProp]))
        .attr('style', (d) => `rotate: ${(-180 * d.startAngle) / Math.PI}deg`)
        .attr('d', (d) => this.chartVars.innerArc(d))
        .attr('id', (d, idx) => `arc-inner-${idx}`)
        .attr('stroke-width', 6)
        .attr('class', 'chart--transparent-stroke')
        .attr('opacity', 0);

      // arc labels
      innerLabels = this.svg
        .append('g')
        .selectAll()
        .data(this.chartVars.customAxisInner)
        .join('g')
        .attr('data-donut-label', '')
        .attr('text-anchor', (d, idx) => {
          const medianRadian =
          dataPieInner[idx].startAngle / 2 + dataPieInner[idx].endAngle / 2;
          if (medianRadian <= Math.PI) {
            return 'start';
          } else if (( medianRadian >= 1.95 * Math.PI) && ( dataPieInner[0].endAngle > 0.33 ) ) {
            return 'start';
          } else {
            return 'end';
          }
        })
        .attr('transform', (d, idx) => {
          const medianRadian = dataPieInner[idx].startAngle / 2 + dataPieInner[idx].endAngle / 2;

          const coords = this.chartVars.arc
            .centroid(dataPieInner[idx])
            .map((vector) => vector * .5);
          if (medianRadian <= 0.5 * Math.PI || medianRadian >= 1.5 * Math.PI) {
            coords[1] = coords[1] + 2 - 14 * this.chartVars.customAxisInner[idx].length;
          } else {
            coords[1] = coords[1] - 8;
          }

          // if
          if (medianRadian <= Math.PI) {
            coords[0] = coords[0] - 24;
          } else if (( medianRadian >= 1.95 * Math.PI) && ( dataPieInner[0].endAngle > 0.33 ) ) {
            coords[0] = coords[0] - 24;
          } else {
            coords[0] = coords[0] + 18;
          }

          if (dataPieInner[idx]?.data?.xOffset) {
            coords[0] = coords[0] + dataPieInner[idx].data.xOffset;
          }
          if (dataPieInner[idx]?.data?.yOffset) {
            coords[1] = coords[1] + dataPieInner[idx].data.yOffset;
          }

          return `translate(${coords})`;
        })
        .attr('fill', (d) => {
          const paletteText = d.map((d) => d.text).join('<br>');
          return this.chartVars.palettes.paletteTwo(paletteText);
        })
        .selectAll()
        .data((d) => d)
        .join('text')
        .attr('opacity', 0)
        .attr('y', (_, i) => 14 * i + 4)
        .text((d) => this.config.chartLegend?.length > 0 ? '' : d.text)
        .attr('class', 'chart--text-xs');

      innerPercentages = this.svg
        .append('g')
        .selectAll()
        .data(dataPieInner)
        .join('text')
        .attr('transform', (d, i) => {
          const medianRadian = d.startAngle / 2 + d.endAngle / 2;
          const coords = this.chartVars.arc.centroid(d).map((vector) => vector * .5);
          if (medianRadian >= 0.5 * Math.PI && medianRadian <= 1.5 * Math.PI) {
            coords[1] = coords[1] - 12 + 13 * this.chartVars.customAxisInner[i].length;
          } else {
            coords[1] = coords[1] + 12;
          }

          if (medianRadian <= Math.PI) {
            coords[0] = coords[0] - 24;
          } else if (( medianRadian >= 1.95 * Math.PI) && ( dataPieInner[0].endAngle > 0.33 ) ) {
            coords[0] = coords[0] - 24;
          } else {
            coords[0] = coords[0] + 18;
          }

          if (d?.data?.xOffset) {
            coords[0] = coords[0] + d.data.xOffset;
          }
          if (d?.data?.yOffset) {
            coords[1] = coords[1] + d.data.yOffset;
          }

          return `translate(${coords})`;
        })
        .text((d) => `${d.data[this.chartVars.xGroupProp]}%`)
        .attr('opacity', 0)
        .attr('fill', (d) => this.chartVars.palettes.paletteTwo(d.data[this.chartVars.xProp]))
        .attr('dominant-baseline', (d) => {
          const medianRadian = d.startAngle / 2 + d.endAngle / 2;
          if (medianRadian <= 0.5 * Math.PI || medianRadian >= 1.5 * Math.PI) {
            return 'baseline';
          } else {
            return 'hanging';
          }
        })
        .attr('text-anchor', (d) => {
          const medianRadian = d.startAngle / 2 + d.endAngle / 2;
          if (medianRadian <= Math.PI) {
            return 'start';
          } else if (( medianRadian >= 1.95 * Math.PI) && ( dataPieInner[0].endAngle > 0.33 ) ) {
            return 'start';
          } else {
            return 'end';
          }
        })
        .attr('class', 'chart--text-m')
        .attr('style', 'font-weight: 600;');
    }

    this.isElementInViewOnce(
      this.ui.d3Selection,
      () => {
        arcs
          .attr('data-bar-arc', '') // "data-bar-arc" attribute helps selectors know this is an arc
          .attr('data-bar-arc-outer', '')
          .attr('data-bar-index', (d) => d.index) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
          .transition(this.animationSettings.animationGrowTransitionName)
          .duration(this.animationSettings.animationDuration)
          .delay((d) => this.animationSettings.animationStartDelay + d.index * this.animationSettings.animationStagger)
          .attr('style', 'rotate: 0deg')
          .attr('opacity', (_, idx) =>this.data[idx].dupe ? 0 : 1);

        if (innerArcs) {
          innerArcs
            .attr('data-bar-arc', '')
            .attr('data-bar-arc-inner', '')
            .attr('data-bar-index', (d) => d.index)
            .transition(this.animationSettings.animationGrowTransitionName)
            .duration(this.animationSettings.animationDuration)
            .delay((d) => this.animationSettings.animationStartDelay + d.index * this.animationSettings.animationStagger)
            .attr('style', 'rotate: 0deg')
            .attr('opacity', 1);
        }

        labels
          .attr('data-bar-label', '')
          .attr('data-bar-label-outer', '')
          .attr('data-bar-index', (d) => d.index)
          .transition(this.animationSettings.animationGrowTransitionName)
          .duration(this.animationSettings.animationDuration)
          .delay((d) => this.animationSettings.animationStartDelay + (this.animationSettings.animationSecondaryDelay || 0) + d.index * this.animationSettings.animationStagger)
          .attr('opacity', 1);

        if (innerLabels) {
          innerLabels
            .attr('data-bar-label', '')
            .attr('data-bar-label-inner', '')
            .attr('data-bar-index', (d) => d.index)
            .transition(this.animationSettings.animationGrowTransitionName)
            .duration(this.animationSettings.animationDuration)
            .delay((d) => this.animationSettings.animationStartDelay + (this.animationSettings.animationSecondaryDelay || 0) + d.index * this.animationSettings.animationStagger)
            .attr('opacity', 1);
        }

        percentages
          .attr('data-bar-percentage', '')
          .attr('data-bar-percentage-outer', '')
          .attr('data-bar-index', (d) => d.index)
          .transition(this.animationSettings.animationGrowTransitionName)
          .duration(this.animationSettings.animationDuration)
          .delay((d) => this.animationSettings.animationStartDelay + (this.animationSettings.animationSecondaryDelay || 0) + d.index * this.animationSettings.animationStagger)
          .attr('opacity', 1);

        if (innerPercentages) {
          innerPercentages
            .attr('data-bar-percentage', '')
            .attr('data-bar-percentage-inner', '')
            .attr('data-bar-index', (d) => d.index)
            .transition(this.animationSettings.animationGrowTransitionName)
            .duration(this.animationSettings.animationDuration)
            .delay((d) => this.animationSettings.animationStartDelay + (this.animationSettings.animationSecondaryDelay || 0) + d.index * this.animationSettings.animationStagger)
            .attr('opacity', 1);
        }

        const legend = d3
          .select(thisScope.element)
          .selectAll('.chart--legend-item');

        [arcs, labels, percentages, legend].forEach((element) => {
          element.on('mouseover', function(event) {
            const allOuterArcsExceptMatchingOne = `[data-bar-arc-outer][data-bar-index]:not([data-bar-arc][data-bar-index="${
              event.target.getAttribute('data-bar-index')}"])`;

            d3.select(thisScope.element)
              .selectAll(allOuterArcsExceptMatchingOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d.data[thisScope.chartVars.xProp]));

            if (thisScope.ringMap) {
              const allInnerArcsExceptMatchingOne = `[data-bar-arc-inner][data-bar-index]:not([data-bar-arc][data-bar-index="${
                thisScope.ringMap.outer[event.target.getAttribute('data-bar-index')]}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerArcsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d.data[thisScope.chartVars.xProp]));
            }

            const allOuterSliceLabelsExceptMatchingOne = `[data-bar-label-outer][data-bar-index]:not([data-bar-label][data-bar-index="${
              event.target.getAttribute('data-bar-index')}"])`;

            d3.select(thisScope.element)
              .selectAll(allOuterSliceLabelsExceptMatchingOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .attr('fill', (d) => thisScope.config.chartPaletteTextHighlights[d.index]);

            if (thisScope.ringMap) {
              const allInnerSliceLabelsExceptMatchingOne = `[data-bar-label-inner][data-bar-index]:not([data-bar-label][data-bar-index="${
                thisScope.ringMap.outer[event.target.getAttribute('data-bar-index')]}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSliceLabelsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d, _, nodes) => {
                  const siblingNodes = [...[...nodes].find((node) => node.innerText = d.text).parentElement.children];
                  const paletteString = siblingNodes.map((node) => node.innerText).join('<br>');
                  return thisScope.chartVars.palettes.highlightPaletteTwo(paletteString);
                });
            }

            const allOuterSlicePercentagesExceptMatchingOne = `[data-bar-percentage-outer][data-bar-index]:not([data-bar-percentage][data-bar-index="${
              event.target.getAttribute('data-bar-index')}"])`;

            d3.select(thisScope.element)
              .selectAll(allOuterSlicePercentagesExceptMatchingOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d.data[thisScope.chartVars.xProp]));

            if (thisScope.ringMap) {
              const allInnerSlicePercentagesExceptMatchingOne = `[data-bar-percentage-inner][data-bar-index]:not([data-bar-percentage][data-bar-index="${
                thisScope.ringMap.outer[event.target.getAttribute('data-bar-index')]}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSlicePercentagesExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d.data[thisScope.chartVars.xProp]));
            }

            const allDonutLegendColorsExceptHoveredOne = `.chart--legend-item-color[data-bar-index]:not(.chart--legend-item-color[data-bar-index="${
              event.target.getAttribute('data-bar-index')}"]`;

            d3
              .select(thisScope.element)
              .selectAll(allDonutLegendColorsExceptHoveredOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .style('background-color', (_, index, groups) => groups[index].getAttribute('data-legend-highlight-color'));

            const allDonutLegendLabelsExceptHoveredOne = `.chart--legend-item-label[data-bar-index]:not(.chart--legend-item-label[data-bar-index="${
              event.target.getAttribute('data-bar-index')}"]`;

            d3
              .select(thisScope.element)
              .selectAll(allDonutLegendLabelsExceptHoveredOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .style('color', (d, index, groups) => groups[index].getAttribute('data-legend-text-highlight-color'));
          });

          element.on('mouseout', function(event) {
            const allOuterArcsExceptMatchingOne = `[data-bar-arc][data-bar-index]:not([data-bar-arc][data-bar-index="${
              event.target.getAttribute('data-bar-index')}"])`;

            d3.select(thisScope.element)
              .selectAll(allOuterArcsExceptMatchingOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d.data[thisScope.chartVars.xProp]));

            if (thisScope.ringMap) {
              const allInnerArcsExceptMatchingOne = `[data-bar-arc-inner][data-bar-index]:not([data-bar-arc][data-bar-index="${
                thisScope.ringMap.outer[event.target.getAttribute('data-bar-index')]}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerArcsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d.data[thisScope.chartVars.xProp]));
            }

            const allOuterSliceLabelsExceptMatchingOne = `[data-bar-label-outer][data-bar-index]:not([data-bar-label][data-bar-index="${
              event.target.getAttribute('data-bar-index')}"])`;

            d3.select(thisScope.element)
              .selectAll(allOuterSliceLabelsExceptMatchingOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .attr('fill', (d) => thisScope.config.chartPaletteSecondary[d.index]);

            if (thisScope.ringMap) {
              const allInnerSliceLabelsExceptMatchingOne = `[data-bar-label-inner][data-bar-index]:not([data-bar-label][data-bar-index="${
                thisScope.ringMap.outer[event.target.getAttribute('data-bar-index',
                )]}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSliceLabelsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d, _, nodes) => {
                  const siblingNodes = [...[...nodes].find((node) => node.innerText = d.text).parentElement.children];
                  const paletteString = siblingNodes.map((node) => node.innerText).join('<br>');
                  return thisScope.chartVars.palettes.paletteTwo(paletteString);
                });
            }

            const allOuterSlicePercentagesExceptMatchingOne = `[data-bar-percentage][data-bar-index]:not([data-bar-percentage][data-bar-index="${
              event.target.getAttribute('data-bar-index')}"])`;

            d3.select(thisScope.element)
              .selectAll(allOuterSlicePercentagesExceptMatchingOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d.data[thisScope.chartVars.xProp]));

            if (thisScope.ringMap) {
              const allInnerSlicePercentagesExceptMatchingOne = `[data-bar-percentage][data-bar-index]:not([data-bar-percentage][data-bar-index="${
                thisScope.ringMap.outer[event.target.getAttribute('data-bar-index')]}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSlicePercentagesExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d.data[thisScope.chartVars.xProp]));
            }

            const allDonutLegendColorsExceptHoveredOne = `.chart--legend-item-color[data-bar-index]:not(.chart--legend-item-color[data-bar-index="${
              event.target.getAttribute('data-bar-index')}"]`;

            d3
              .select(thisScope.element)
              .selectAll(allDonutLegendColorsExceptHoveredOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .style('background-color', (_, index, groups) => groups[index].getAttribute('data-legend-color'));

            const allDonutLegendLabelsExceptHoveredOne = `.chart--legend-item-label[data-bar-index]:not(.chart--legend-item-label[data-bar-index="${
              event.target.getAttribute('data-bar-index')}"]`;

            d3
              .select(thisScope.element)
              .selectAll(allDonutLegendLabelsExceptHoveredOne)
              .transition(thisScope.animationSettings.animationHoverTransitionName)
              .duration(thisScope.animationSettings.animationHoverDuration)
              .style('color', (d, index, groups) => groups[index].getAttribute('data-legend-text-color'));
          });
        });

        if (this.ringMap) {
          [innerArcs, innerLabels, innerPercentages].forEach((element) => {
            element.on('mouseover', function(event) {
              const allInnerArcsExceptMatchingOne = `[data-bar-arc-inner][data-bar-index]:not([data-bar-arc][data-bar-index="${
                event.target.getAttribute('data-bar-index')}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerArcsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d.data[thisScope.chartVars.xProp]));

              let allOuterArcsExceptMatchingOne = '[data-bar-arc-outer][data-bar-index]';
              thisScope.ringMap.inner[event.target.getAttribute('data-bar-index')].forEach((d) => {
                allOuterArcsExceptMatchingOne += `:not([data-bar-arc][data-bar-index="${d}"])`;
              });

              d3.select(thisScope.element)
                .selectAll(allOuterArcsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d.data[thisScope.chartVars.xProp]));

              const allInnerSliceLabelsExceptMatchingOne = `[data-bar-label-inner][data-bar-index]:not([data-bar-label][data-bar-index="${
                event.target.getAttribute('data-bar-index')}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSliceLabelsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d, _, nodes) => {
                  const siblingNodes = [...[...nodes].find((node) => node.innerText = d.text).parentElement.children];
                  const paletteString = siblingNodes.map((node) => node.innerText).join('<br>');
                  return thisScope.chartVars.palettes.highlightPaletteTwo(paletteString);
                });

              let allOuterSliceLabelsExceptMatchingOne = '[data-bar-label-outer][data-bar-index]';
              thisScope.ringMap.inner[event.target.getAttribute('data-bar-index')].forEach((d) => {
                allOuterSliceLabelsExceptMatchingOne += `:not([data-bar-label][data-bar-index="${d}"])`;
              });

              d3.select(thisScope.element)
                .selectAll(allOuterSliceLabelsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.config.chartPaletteTextHighlights[d.index]);

              const allInnerSlicePercentagesExceptMatchingOne = `[data-bar-percentage-inner][data-bar-index]:not([data-bar-percentage][data-bar-index="${
                event.target.getAttribute('data-bar-index')}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSlicePercentagesExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d.data[thisScope.chartVars.xProp]));

              let allOuterSlicePercentagesExceptMatchingOne = '[data-bar-percentage-outer][data-bar-index]';
              thisScope.ringMap.inner[event.target.getAttribute('data-bar-index')].forEach((d) => {
                allOuterSlicePercentagesExceptMatchingOne += `:not([data-bar-percentage][data-bar-index="${d}"])`;
              });

              d3.select(thisScope.element)
                .selectAll(allOuterSlicePercentagesExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d.data[thisScope.chartVars.xProp]));
            });

            element.on('mouseout', function(event) {
              const allInnerArcsExceptMatchingOne = `[data-bar-arc-inner][data-bar-index]:not([data-bar-arc][data-bar-index="${event.target.getAttribute(
                'data-bar-index',
              )}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerArcsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d.data[thisScope.chartVars.xProp]));

              let allOuterArcsExceptMatchingOne = '[data-bar-arc-outer][data-bar-index]';
              thisScope.ringMap.inner[event.target.getAttribute('data-bar-index')].forEach((d) => {
                allOuterArcsExceptMatchingOne += `:not([data-bar-arc][data-bar-index="${d}"])`;
              });

              d3.select(thisScope.element)
                .selectAll(allOuterArcsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d.data[thisScope.chartVars.xProp]));

              const allInnerSliceLabelsExceptMatchingOne = `[data-bar-label-inner][data-bar-index]:not([data-bar-label][data-bar-index="${
                event.target.getAttribute('data-bar-index')}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSliceLabelsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d, _, nodes) => {
                  const siblingNodes = [...[...nodes].find((node) => node.innerText = d.text).parentElement.children];
                  const paletteString = siblingNodes.map((node) => node.innerText).join('<br>');
                  return thisScope.chartVars.palettes.paletteTwo(paletteString);
                });

              let allOuterSliceLabelsExceptMatchingOne = '[data-bar-label-outer][data-bar-index]';
              thisScope.ringMap.inner[event.target.getAttribute('data-bar-index')].forEach((d) => {
                allOuterSliceLabelsExceptMatchingOne += `:not([data-bar-label][data-bar-index="${d}"])`;
              });

              d3.select(thisScope.element)
                .selectAll(allOuterSliceLabelsExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.config.chartPaletteSecondary[d.index]);

              const allInnerSlicePercentagesExceptMatchingOne = `[data-bar-percentage][data-bar-index]:not([data-bar-percentage][data-bar-index="${
                event.target.getAttribute('data-bar-index')}"])`;

              d3.select(thisScope.element)
                .selectAll(allInnerSlicePercentagesExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d.data[thisScope.chartVars.xProp]));

              let allOuterSlicePercentagesExceptMatchingOne = '[data-bar-percentage-outer][data-bar-index]';
              thisScope.ringMap.inner[event.target.getAttribute('data-bar-index')].forEach((d) => {
                allOuterSlicePercentagesExceptMatchingOne += `:not([data-bar-percentage][data-bar-index="${d}"])`;
              });

              d3.select(thisScope.element)
                .selectAll(allOuterSlicePercentagesExceptMatchingOne)
                .transition(thisScope.animationSettings.animationHoverTransitionName)
                .duration(thisScope.animationSettings.animationHoverDuration)
                .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d.data[thisScope.chartVars.xProp]));
            });
          });
        }
      },
      this.animationSettings.animationInViewThreshold);
  }

  buildLineChart() { // lc2
    const thisScope = this;

    const tooltip = d3.select(this.ui.d3Selection)
      .append('div')
      .style('visibility', 'hidden')
      .attr('class', 'tooltip')
      .style('background-color', 'white')
      .style('border', 'solid')
      .style('border-width', '2px')
      .style('border-radius', '4px')
      .style('padding', '15px')
      .style('position', 'absolute');

    const mouseover = function() {
      // eslint-disable-next-line
      const haloTarget = this.parentElement.parentElement.querySelector('.chart--halo-dots').querySelector(`[dot-index="${this.getAttribute('dot-index')}"]`);
      // eslint-disable-next-line
      const frontTarget = this.parentElement.parentElement.querySelector('.chart--front-dots').querySelector(`[dot-index="${this.getAttribute('dot-index')}"]`)

      tooltip
        .style('visibility', 'visible');
      d3.select(haloTarget)
        .style('opacity', 1);
      d3.select(frontTarget)
        .style('opacity', 1);
    };

    const mousemove = function(event, d) {
      // eslint-disable-next-line
      const nearEdge = parseInt(this.getAttribute('cx')) > .75 * thisScope.chartVars.width;
      // eslint-disable-next-line
      const xPosition = ( ( this.getAttribute('cx') * thisScope.ui.d3Selection.clientWidth) / thisScope.chartVars.width)
      tooltip
        .style('left', nearEdge ? '' : xPosition + 'px')
        .style('right', nearEdge ? (thisScope.ui.d3Selection.clientWidth - xPosition) + 'px': '')
        // eslint-disable-next-line
        .style('top', ( ( ( parseInt(this.getAttribute('cy')) + 5 ) * thisScope.ui.d3Selection.clientHeight) / thisScope.chartVars.height) + 'px')
        .html(() => {
          return thisScope.chartVars.axisType === 'date' ?
            `<span class="chart--tip-bold">${new Date(d[thisScope.chartVars.xProp]).toLocaleDateString()}</span><br>
              <span class="chart--tip-cap">${d[thisScope.chartVars.yGroupProp]}</span>: ${d[thisScope.chartVars.yProp]}` :
            `<span class="chart--tip-bold">${d[thisScope.chartVars.yGroupProp]}</span><br>
              <span class="chart--tip-cap">Year ${d[thisScope.chartVars.xProp]}: ${d[thisScope.chartVars.yProp]}`;
        });
    };

    const mouseleave = function() {
      // eslint-disable-next-line
      const haloTarget = this.parentElement.parentElement.querySelector('.chart--halo-dots').querySelector(`[dot-index="${this.getAttribute('dot-index')}"]`);
      // eslint-disable-next-line
      const frontTarget = this.parentElement.parentElement.querySelector('.chart--front-dots').querySelector(`[dot-index="${this.getAttribute('dot-index')}"]`)

      tooltip
        .style('visibility', 'hidden');
      d3.select(haloTarget)
        .style('opacity', 0);
      d3.select(frontTarget)
        .style('opacity', 0);
    };

    // y axis and gridlines
    const tickValues = [20, 40, 60, 80, 100];
    if (this.options?.customLine?.axis === 'y') {
      tickValues.push(this.options.customLine.value);
    }
    const yAxis = d3.axisLeft(this.chartVars.y).tickValues(tickValues).tickFormat((d) => d + '%');
    this.svg
      .append('g')
      .attr('transform', `translate(${this.chartVars.margins.left},0)`)
      .call(yAxis)
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('.tick line')
        .attr('x2', this.chartVars.width - this.chartVars.margins.left - this.chartVars.margins.right)
        .attr('stroke-opacity', 0.1)
        .style('stroke-dasharray', (d) => {
          return this.options?.customLine?.value === d ? ('5', '5') : 'none';
        }),
      )
      .call((g) => g.selectAll('.tick text')
        .attr('class', () => this.chartVars.axisType === 'date' ? 'chart--text-xs' : 'chart--text-s')
        .attr('opacity', (d) => {
          return this.options?.customLine?.value === d ? 0.5 : 1;
        })
        .attr('transform', 'translate(0,-3)'));

    // dotted line extensions
    if (this.secondarySeries) {
      Object.entries(this.secondarySeries).forEach(([key, val]) => {
        this.svg.append('path')
          .attr('transform', `translate(${this.chartVars.margins.left},0)`)
          .attr('fill', 'none')
          .attr('stroke', () => this.curveSeries ? this.chartVars.palettes.highlightPaletteOne(key) : this.chartVars.palettes.paletteOne(key))
          .style('stroke-dasharray', ('5, 5'))
          .attr('stroke-width', 1.5)
          .attr('d', () => {
            return this.chartVars.line(val);
          });
      });
    }

    // lines and dots
    Object.entries(this.series).forEach(([key, val], groupIdx) => {
      this.svg.append('path')
        .attr('transform', `translate(${this.chartVars.margins.left},0)`)
        .attr('fill', 'none')
        .attr('stroke', () => this.curveSeries ? this.chartVars.palettes.highlightPaletteOne(key) : this.chartVars.palettes.paletteOne(key))
        .attr('stroke-width', 1.5)
        .attr('d', () => this.chartVars.line(val));
      // halo dot (for white and gray effect on hover)
      const dotGroups = this.svg.append('g')
        .attr('class', 'chart--dot-groups');
      // invisible dot (for larger hover effect radius)
      dotGroups.append('g')
        .attr('class', 'chart--invisible-dots')
        .attr('group-index', groupIdx)
        .selectAll()
        .data(Object.values(val))
        .join('circle')
        .attr('dot-index', (key, i) => i)
        .attr('fill', 'white')
        .attr('opacity', 0)
        .attr('cx', (d) => {
          const xValue = this.chartVars.axisType === 'date' ? this.parseDate(d[this.chartVars.xProp]) : this.parseNumber(d[this.chartVars.xProp]);
          return this.chartVars.x(xValue) + this.chartVars.margins.left;
        })
        .attr('cy', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) )
        .attr('r', this.options?.radius?.hover || 10)
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave);
      dotGroups.append('g')
        .attr('class', 'chart--back-dots')
        .attr('group-index', groupIdx)
        .selectAll()
        .data(Object.values(val))
        .join('circle')
        .attr('dot-index', (key, i) => i)
        .attr('fill', () => this.curveSeries ? this.chartVars.palettes.highlightPaletteOne(key) : this.chartVars.palettes.paletteOne(key))
        .attr('cx', (d) => {
          const xValue = this.chartVars.axisType === 'date' ? this.parseDate(d[this.chartVars.xProp]) : this.parseNumber(d[this.chartVars.xProp]);
          return this.chartVars.x(xValue) + this.chartVars.margins.left;
        })
        .attr('cy', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) )
        .attr('r', this.options?.radius?.dot || 3)
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave);
      dotGroups.append('g')
        .attr('class', 'chart--halo-dots')
        .attr('group-index', groupIdx)
        .selectAll()
        .data(Object.values(val))
        .join('circle')
        .attr('dot-index', (_, i) => i)
        .attr('fill', 'white')
        .attr('stroke', '#e6e6e6')
        .attr('stroke-width', 0.5)
        .attr('opacity', 0)
        .attr('cx', (d) => {
          const xValue = this.chartVars.axisType === 'date' ? this.parseDate(d[this.chartVars.xProp]) : this.parseNumber(d[this.chartVars.xProp]);
          return this.chartVars.x(xValue) + this.chartVars.margins.left;
        })
        .attr('cy', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) )
        .attr('r', this.options?.radius?.halo || 5)
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave);
      // front dot (path-colored dot, appears on hover; necessary for proper paint order)
      dotGroups.append('g')
        .attr('class', 'chart--front-dots')
        .attr('group-index', groupIdx)
        .selectAll()
        .data(Object.values(val))
        .join('circle')
        .attr('dot-index', (_, i) => i)
        .attr('opacity', 0)
        .attr('fill', () => this.curveSeries ? this.chartVars.palettes.highlightPaletteOne(key) : this.chartVars.palettes.paletteOne(key))
        .attr('cx', (d) => {
          const xValue = this.chartVars.axisType === 'date' ? this.parseDate(d[this.chartVars.xProp]) : this.parseNumber(d[this.chartVars.xProp]);
          return this.chartVars.x(xValue) + this.chartVars.margins.left;
        })
        .attr('cy', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) )
        .attr('r', this.options?.radius?.dot || 3)
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave);
    });

    // curved line overlay
    if (this.curveSeries) {
      Object.entries(this.curveSeries).forEach(([key, val], groupIdx) => {
        this.svg.append('path')
          .attr('transform', `translate(${this.chartVars.margins.left},0)`)
          .attr('fill', 'none')
          .attr('stroke', this.chartVars.palettes.paletteOne(key))
          .attr('stroke-width', 1.5)
          .attr('d', () => this.chartVars.curve(val));

        // final dot
        this.svg.append('g')
          .attr('class', 'chart--dot-groups')
          .append('g')
          .attr('class', 'chart--back-dots')
          .attr('group-index', groupIdx)
          .selectAll()
          .data(Object.values(val))
          .join('circle')
          .attr('dot-index', (_, i) => i)
          .attr('fill', (_, idx, dots) => {
            return dots.length - 1 <= idx ? this.chartVars.palettes.paletteOne(key) : 'none';
          })
          .attr('cx', (d) => {
            const xValue = this.chartVars.axisType === 'date' ? this.parseDate(d[this.chartVars.xProp]) : this.parseNumber(d[this.chartVars.xProp]);
            return this.chartVars.x(xValue) + this.chartVars.margins.left;
          })
          .attr('cy', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) )
          .attr('r', this.options?.radius?.dot || 3);
      });

      if (this.options.curveLabels) {
        this.options.curveLabels.forEach((label) => {
          const lastCoords =[...this.curveSeries[label[this.chartVars.yGroupProp]]].pop();
          const x = this.chartVars.x(this.chartVars.axisType === 'date' ? this.parseDate(lastCoords[this.chartVars.xProp]) : this.parseNumber(lastCoords[this.chartVars.xProp]));
          const y = this.chartVars.y(this.parseNumber(lastCoords[this.chartVars.yProp]));
          this.svg.append('g')
            .attr('class', 'chart--curve-label')
            .attr('transform', `translate(${x + this.chartVars.margins.left + 8},${y + 20})`)
            .selectAll()
            .data([...label.title.split('<br>'), label.description])
            .join('text')
            .attr('class', (_, idx, group) => group.length -1 > idx ? 'chart--text-s f-sans-bold' : 'chart--text-s')
            .text((d) => d)
            .attr('y', (_, i, group) => group.length -1 > i ? i * 18 : i * 18 + 4);
        });
      }
    }

    // x axis line
    this.svg
      .append('rect')
      .attr('width', this.chartVars.width - this.chartVars.margins.left)
      .attr('height', 1)
      .attr('x', this.chartVars.margins.left)
      .attr('y', this.chartVars.height - this.chartVars.margins.bottom);

    // x axis labels
    this.svg
      .append('g')
      .attr('transform', `translate(${this.chartVars.margins.left},0)`)
      .selectAll()
      .data(this.chartVars.ticks.x)
      .join('text')
      .text((d) => this.chartVars.axisType === 'date' ? new Date(`${d}T00:00:00`).toLocaleDateString() : d)
      .attr('y', this.chartVars.axisType === 'date' ? this.chartVars.height - this.chartVars.margins.bottom + 14 : this.chartVars.height - this.chartVars.margins.bottom + 21)
      .attr('class', () => this.chartVars.axisType === 'date' ? 'chart--text-xs' : 'chart--text-s')
      .attr('data-bar-index', (d, i) => i)
      .attr('x', ((d) => this.chartVars.axisType === 'date' ? this.chartVars.x(new Date(d)) : this.chartVars.x(d)))
      .attr('text-anchor', 'middle');

    // x axis title
    if (this.options.xAxis.title) {
      this.svg
        .append('text')
        .attr('class', 'chart--text-s')
        .attr('y', this.chartVars.height - this.chartVars.margins.bottom + 44)
        .attr('x', (this.chartVars.width - this.chartVars.margins.left) / 2 + this.chartVars.margins.left)
        .text(this.options.xAxis.title)
        .attr('text-anchor', 'middle');
    }

    // y axis title
    if (this.options.yAxis.title) {
      this.svg
        .append('text')
        .attr('class', 'chart--text-s')
        .attr('y', this.chartVars.margins.left - 55)
        .attr('x', -(this.chartVars.height - this.chartVars.margins.bottom) / 2 - this.chartVars.margins.top)
        .attr('transform', 'rotate(-90)')
        .text(this.options.yAxis.title)
        .attr('text-anchor', 'middle');
    }

    // TODO?: Line chart has no animations designed or configured.
  }


  buildBarChartVerticalBars() { // vb2
    // Save correct this scope to make "this" resolution easier
    const thisScope = this;

    // generates/appends the bars
    const bars = this.svg
      .append('g')
      .selectAll()
      .data(this.data)
      .join('rect')
      .attr('x', (d) => this.chartVars.x(d[this.chartVars.xProp]) + 8)
      .attr('y', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) + (
        this.chartVars.y(this.config.customFloor) -
        this.chartVars.y(this.parseNumber(d[this.chartVars.yProp]))))
      .attr('width', this.chartVars.x.bandwidth() - 16)
      .attr('height', 0)
      .attr('fill', (d) => this.chartVars.palettes.paletteOne(d[this.chartVars.xProp]));

    this.svg
      .append('rect')
      .attr('width', this.chartVars.width)
      .attr('height', 1)
      .attr('x', 0)
      .attr('y', this.chartVars.height - this.chartVars.margins.bottom);

    // generates/appends bar labels
    const percentages = this.svg
      .append('g')
      .selectAll()
      .data(this.data)
      .join('text')
      .text((d) => `${d[this.chartVars.yProp]}`)
      .attr('text-anchor', 'middle')
      .attr('class', 'chart--text-s')
      .attr(
        'x',
        (d) =>
          this.chartVars.x(d[this.chartVars.xProp]) +
          this.chartVars.x.bandwidth() / 2,
      )
      .attr('y', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) - 10 + (
        this.chartVars.y(this.config.customFloor) -
        this.chartVars.y(this.parseNumber(d[this.chartVars.yProp]))
      ))
      .attr('fill', (d) => {
        return this.chartVars.palettes.paletteTwo(d[this.chartVars.xProp]);
      });

    // generates/appends axis labels
    const axisLabels = this.svg
      .append('g')
      .selectAll()
      .data(this.chartVars.customAxis)
      .join('g')
      .attr('data-bar-title', '')
      .attr('data-bar-index', (d, i) => i) // Workaround: Since this is missing from the data, use the index of the iteration to resolve it preventing d3 logic changes.
      .attr('transform', (d) => {
        return `translate(${
          this.chartVars.x(d.join('<br>')) + this.chartVars.x.bandwidth() / 2
        },${this.chartVars.height - this.chartVars.margins.bottom + 30})`;
      })
      .selectAll()
      .data((d) => d)
      .join('text')
      .text((d) => d)
      .attr('text-anchor', 'middle')
      .attr('class', 'chart--text-s')
      .attr('y', (_, i) => 22 * i);

    this.isElementInViewOnce(this.ui.d3Selection, () => {
      // Animate bar width
      bars
        .attr('data-bar-rect', '') // "data-bar-rect" attribute helps selectors know this is a bar
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          // Animation stagger is determined by looking up the this.animationSettings.animationIndexProperty on the data. For example, in a horizontal bar chart the leftmost bar is has an index of 0, following that 1, 2, etc.
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr('height', (d) => {
          return (
            this.chartVars.y(this.config.customFloor) -
            this.chartVars.y(this.parseNumber(d[this.chartVars.yProp]))
          );
        })
        .attr('y', (d) => this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])));

      // Animate chart percentage labels
      percentages
        .attr('data-bar-percent', '') // "data-bar-percent" attribute helps selectors know this is a percent
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr('y', (d) => (this.chartVars.y(this.parseNumber(d[this.chartVars.yProp])) - 10));

      [bars, percentages, axisLabels].forEach((element) => element.on('mouseover', function(event) {
        // Workaround: data-bar-index is either the element that is mousedover or the parent of that element - exists because axis label data has no indexes so it's resolved from the parent
        const hoverIndex = event.target.getAttribute('data-bar-index') || event.target.parentNode.getAttribute('data-bar-index');
        const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${hoverIndex}"])`;

        // Animate bars on hover (selects all non-hovered bars)
        d3
          .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
          .selectAll(allBarsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d[thisScope.chartVars.xProp]));

        const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${hoverIndex}"])`;

        // // Animate percent labels on hover (selects all non-hovered percent labels)
        d3
          .select(thisScope.element)
          .selectAll(allBarPercentagesExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d[thisScope.chartVars.xProp]));

        const allBarTitlesExceptHoveredOne = `[data-bar-title][data-bar-index]:not([data-bar-title][data-bar-index="${hoverIndex}"])`;

        // // Animate axis bar labels on hover
        d3
          .select(thisScope.element)
          .selectAll(allBarTitlesExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d.join('<br>')));
      })
        .on('mouseout', function(event) {
          // Workaround: data-bar-index is either the element that is mousedover or the parent of that element - exists because axis label data has no indexes so it's resolved from the parent
          const hoverIndex = event.target.getAttribute('data-bar-index') || event.target.parentNode.getAttribute('data-bar-index');

          // Animate bars on mouseout (selects all non-hovered bars)
          const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${hoverIndex}"])`;
          d3
            .select(thisScope.element)
            .selectAll(allBarsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d[thisScope.chartVars.xProp]));

          const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${hoverIndex}"])`;

          // Animate percent labels on mouseout (selects all non-hovered percent labels)
          d3
            .select(thisScope.element)
            .selectAll(allBarPercentagesExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d[thisScope.chartVars.xProp]));


          const allBarTitlesExceptHoveredOne = `[data-bar-title][data-bar-index]:not([data-bar-title][data-bar-index="${hoverIndex}"])`;

          // // Animate text axis labels on mouseout
          d3
            .select(thisScope.element)
            .selectAll(allBarTitlesExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d.join('<br>')));
        }));
    }, this.animationSettings.animationInViewThreshold);
  }

  buildBarChartHorizontalBars() { // hb2
    // Save correct this scope to make "this" resolution easier
    const thisScope = this;

    // generates/appends the bars
    const bars = this.svg
      .append('g')
      .selectAll()
      .data(this.data)
      .join('rect')
      .attr('x', this.chartVars.margins.left)
      .attr('y', (d) => this.chartVars.y(d[this.chartVars.yProp]) + 29)
      .attr('height', 20)
      .attr('width', 0)
      .attr('fill', (d) => this.chartVars.palettes.paletteOne(d[this.chartVars.yProp]));

    // generates/appends percentages
    const percentages = this.svg
      .append('g')
      .selectAll()
      .data(this.data)
      .join('text')
      .text((d) => `${d[this.chartVars.xProp]}`)
      .attr('class', 'chart--text-xs')
      .attr('y', (d) => this.chartVars.y(d[this.chartVars.yProp]) + 43)
      .attr('x', 8)
      .attr('fill', (d) => this.chartVars.palettes.paletteTwo(d[this.chartVars.yProp]));

    // generates/appends text labels
    const barTitles = this.svg
      .append('g')
      .selectAll()
      .data(this.data)
      .join('text')
      .attr('class', 'chart--text-s')
      .attr('y', (d) => this.chartVars.y(d[this.chartVars.yProp]) + 18)
      .text((d) => d[this.chartVars.yProp])
      .attr('x', this.chartVars.margins.left)
      .attr('fill', (d) => this.chartVars.palettes.paletteTwo(d[this.chartVars.yProp]));

    // Detect if chart is in view based on passed config and if so start animation
    this.isElementInViewOnce(this.ui.d3Selection, () => {
      this.chartShown = true;
      // Animate bar width
      bars
        .attr('data-bar-rect', '') // "data-bar-rect" attribute helps selectors know this is a bar
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          // Animation stagger is determined by looking up the this.animationSettings.animationIndexProperty on the data. For example, in a horizontal bar chart the leftmost bar is has an index of 0, following that 1, 2, etc.
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr(
          'width',
          (d) => this.chartVars.x(0) - this.chartVars.x(this.parseNumber(d[this.chartVars.xProp])),
        );

      // Animate chart percentage labels
      percentages
        .attr('data-bar-percent', '') // "data-bar-percent" attribute helps selectors know this is a percent
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr(
          'x',
          (d) => this.chartVars.x(0) - this.chartVars.x(this.parseNumber(d[this.chartVars.xProp])) + 8,
        );

      barTitles
        .attr('data-bar-title', '')
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]);

      // Attach hover animations on mouseover/mouseout on both the bars and the percentage labels (hovering over a label also shows an animation)
      [bars, percentages, barTitles].forEach((element) => element.on('mouseover', function(event) {
        const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

        // Animate bars on hover (selects all non-hovered bars)
        d3
          .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
          .selectAll(allBarsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d[thisScope.chartVars.yProp]));

        const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

        // Animate percent labels on hover (selects all non-hovered percent labels)
        d3
          .select(thisScope.element)
          .selectAll(allBarPercentagesExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d[thisScope.chartVars.yProp]),
          );

        const allBarTitlesExceptHoveredOne = `[data-bar-title][data-bar-index]:not([data-bar-title][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

        // Animate bar title on hover
        d3
          .select(thisScope.element)
          .selectAll(allBarTitlesExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d[thisScope.chartVars.yProp]),
          );
      })
        .on('mouseout', function(event) {
          // Animate bars on mouseout (selects all non-hovered bars)
          const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${event.target.dataset.barIndex}"])`;
          d3
            .select(thisScope.element)
            .selectAll(allBarsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d[thisScope.chartVars.yProp]));

          const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

          // Animate chart percentage labels
          percentages
            .attr('data-bar-percent', '') // "data-bar-percent" attribute helps selectors know this is a percent
            .attr(
              'data-bar-index',
              (d) => d[thisScope.animationSettings.animationIndexProperty],
            ) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
            .transition(thisScope.animationSettings.animationGrowTransitionName)
            .duration(thisScope.animationSettings.animationDuration)
            .delay((d) => {
              return (
                thisScope.animationSettings.animationStartDelay +
              d[thisScope.animationSettings.animationIndexProperty] *
                thisScope.animationSettings.animationStagger
              );
            })
            .attr(
              'x',
              (d) =>
                thisScope.chartVars.x(0) -
              thisScope.chartVars.x(thisScope.parseNumber(d[thisScope.chartVars.xProp])) +
              8,
            );

          // Animate percent labels on mouseout (selects all non-hovered percent labels)
          d3
            .select(thisScope.element)
            .selectAll(allBarPercentagesExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d[thisScope.chartVars.yProp]));

          const allBarTitlesExceptHoveredOne = `[data-bar-title][data-bar-index]:not([data-bar-title][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

          // Animate bar title labels on mouseout
          d3
            .select(thisScope.element)
            .selectAll(allBarTitlesExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d[thisScope.chartVars.yProp]));
        }));
    }, this.animationSettings.animationInViewThreshold);
  }

  buildBarChartZigZags() { // zz2
    const thisScope = this;

    // y axis labels
    this.svg
      .append('g')
      .selectAll()
      .data(this.series)
      .join('g')
      .attr('transform', (d) => `translate(0,${this.chartVars.y(parseInt(d.key, 10))})`)
      .selectAll()
      .data((d) => d)
      .join('text')
      .attr('x', this.chartVars.margins.left - 12)
      .attr('text-anchor', 'end')
      .attr('y', 44)
      .text((d) => d[this.chartVars.yProp])
      .attr('style', 'font-size: 12px')
      .attr('fill', '#042A2D')
      .attr('dominant-baseline', 'middle');

    // bars
    const bars = this.svg
      .append('g')
      .selectAll()
      .data(this.series)
      .join('g')
      .attr('transform', (d) => `translate(${this.chartVars.margins.left},${this.chartVars.y(parseInt(d.key))})`)
      .selectAll()
      .data((d) => d)
      .join('rect')
      .attr('x', (d, i) => this.chartVars.x(d.start) + i * 11)
      .attr('y', 26)
      .attr('height', 36)
      .attr('width', 0)
      .attr('fill', (d) => this.chartVars.palettes.paletteOne(d[this.chartVars.xGroupProp]));

    // text superimposed on bars
    const percentages = this.svg
      .append('g')
      .selectAll()
      .data(this.series)
      .join('g')
      .attr('transform', (d) => `translate(${this.chartVars.margins.left},${this.chartVars.y(parseInt(d.key))})`)
      .selectAll()
      .data((d) => d)
      .join('text')
      .text((d) => `${d[this.chartVars.xProp]}`)
      .attr('style', 'font-size: 12px')
      .attr('x', (d, i) => this.chartVars.x(d.start) + i * 11 + 10)
      .attr('y', 45)
      .attr('fill', (d) => this.chartVars.palettes.paletteTwo(d[this.chartVars.xGroupProp]))
      .attr('dominant-baseline', 'middle')
      .attr('opacity', 0);

    // text above top bars
    const topLabels = this.svg
      .append('g')
      .attr('transform', () => `translate(${this.chartVars.margins.left},${this.chartVars.margins.top})`)
      .selectAll()
      .data(this.series[0])
      .join('text')
      .text((d) => d[this.chartVars.xGroupProp])
      .attr('style', 'font-size: 12px')
      .attr('x', (d, i) => this.chartVars.x(d.start) + i * 11 + (i === 0 ? 10 : 0))
      .attr('y', 10)
      .attr('fill', '#042A2D')
      .attr('dominant-baseline', 'middle')
      .attr('opacity', 0);

    // y axis line, must go above bars
    this.svg
      .append('rect')
      .attr('height', this.chartVars.height)
      .attr('width', 1)
      .attr('x', this.chartVars.margins.left)
      .attr('y', 0)
      .attr('fill', '#042A2D');

    // zigzag lines
    const haloLines = this.svg
      .append('g')
      .selectAll()
      .data(this.lineSeries)
      .join('path')
      .attr('stroke', '#ff0000')
      .attr('stroke-width', 7)
      .attr('fill', 'none')
      .attr('transform', (d, i) => `translate(${i * 11 + 6},0)`)
      .attr('d', (d) => this.chartVars.line(d))
      .attr('opacity', 0);
    const lines = this.svg
      .append('g')
      .selectAll()
      .data(this.lineSeries)
      .join('path')
      .attr('stroke', (d) => this.chartVars.palettes.paletteOne(d.key))
      .attr('fill', 'none')
      .attr('transform', (d, i) => `translate(${i * 11 + 6},0)`)
      .attr('d', (d) => this.chartVars.line(d))
      .attr('opacity', 0);

    // animations
    this.isElementInViewOnce(this.ui.d3Selection, () => {
      // animate bar width
      bars
        .attr('data-bar-rect', '')
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty] % this.lineSeries.length)
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + (d[this.animationSettings.animationIndexProperty] % this.lineSeries.length) * this.animationSettings.animationStagger;
        } )
        .attr('width', (d) => this.chartVars.x(this.parseNumber(d[this.chartVars.xProp])));

      lines
        .attr('data-bar-line', '')
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty])
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + 200 + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr('opacity', 1);

      haloLines
        .attr('data-bar-line', '')
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]);

      percentages
        .attr('data-bar-percent', '')
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty] % this.lineSeries.length) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + (d[this.animationSettings.animationIndexProperty] % this.lineSeries.length) * this.animationSettings.animationStagger;
        } )
        .attr('opacity', 1);

      topLabels
        .attr('data-bar-label', '')
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + 200 + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr('opacity', 1);

      [bars, haloLines, lines, percentages, topLabels].forEach((element) => element.on('mouseover', function(event) {
        const hoverIndex = event.target.getAttribute('data-bar-index') || event.target.parentNode.getAttribute('data-bar-index');

        const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${hoverIndex}"])`;
        d3
          .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
          .selectAll(allBarsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => {
            return thisScope.chartVars.palettes.highlightPaletteOne(d[thisScope.chartVars.xGroupProp]);
          });

        const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${hoverIndex}"])`;
        d3
          .select(thisScope.element)
          .selectAll(allBarPercentagesExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d[thisScope.chartVars.xGroupProp]));

        const allBarLabelsExceptHoveredOne = `[data-bar-label][data-bar-index]:not([data-bar-label][data-bar-index="${hoverIndex}"])`;
        d3
          .select(thisScope.element)
          .selectAll(allBarLabelsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', '#CDD4D5');

        const allBarLinesExceptHoveredOne = `[data-bar-line][data-bar-index]:not([data-bar-line][data-bar-index="${hoverIndex}"])`;
        d3
          .select(thisScope.element)
          .selectAll(allBarLinesExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('stroke', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d.key));
      })
        .on('mouseout', function(event) {
          const hoverIndex = event.target.getAttribute('data-bar-index') || event.target.parentNode.getAttribute('data-bar-index');

          const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${hoverIndex}"])`;
          d3
            .select(thisScope.element)
            .selectAll(allBarsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d[thisScope.chartVars.xGroupProp]));

          const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${hoverIndex}"])`;
          d3
            .select(thisScope.element)
            .selectAll(allBarPercentagesExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d[thisScope.chartVars.xGroupProp]));

          const allBarLabelsExceptHoveredOne = `[data-bar-label][data-bar-index]:not([data-bar-label][data-bar-index="${hoverIndex}"])`;
          d3
            .select(thisScope.element)
            .selectAll(allBarLabelsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', '#042A2D');

          const allBarLinesExceptHoveredOne = `[data-bar-line][data-bar-index]:not([data-bar-line][data-bar-index="${hoverIndex}"])`;
          d3
            .select(thisScope.element)
            .selectAll(allBarLinesExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('stroke', (d) => thisScope.chartVars.palettes.paletteOne(d.key));
        }));
    }, this.animationSettings.animationInViewThreshold);
  }

  buildBarChartGroupedHorizontalBars() { // gh2
    // Save correct this scope to make "this" resolution easier
    const thisScope = this;

    // generates/appends the bars
    const bars = this.svg
      .append('g')
      .selectAll()
      .data(this.series)
      .join('g')
      .attr('transform', (d) => `translate(0,${this.chartVars.fy(d.key)})`)
      .selectAll()
      .data((d) => d)
      .join('rect')
      .attr('y', (d) => `${this.chartVars.y(d[this.chartVars.yProp])}`)
      .attr('width', 0)
      .attr('height', 20)
      .attr('fill', (d) => this.chartVars.palettes.paletteOne(d[this.chartVars.yProp]));

    // generates/appends percentages
    const percentages = this.svg
      .append('g')
      .selectAll()
      .data(this.series)
      .join('g')
      .attr('transform', (d) => `translate(0,${this.chartVars.fy(d.key)})`)
      .selectAll()
      .data((d) => d)
      .join('text')
      .text((d) => `${d[this.chartVars.xProp]}`)
      .attr('class', 'chart--text-xs')
      .attr('y', (d) => `${this.chartVars.y(d[this.chartVars.yProp]) + 13}`)
      .attr('x', 0)
      // .attr('x', (d) => this.chartVars.x(this.parseNumber(d[this.chartVars.xProp])) + 8)
      .attr('height', 20)
      .attr('fill', (d) => this.chartVars.palettes.paletteTwo(d[this.chartVars.yProp]));

    // generates/appends group text labels
    const barTitles = this.svg
      .append('g')
      .selectAll()
      .data(this.series)
      .join('text')
      .text((d) => {
        return `${d.key}`;
      })
      .attr('class', 'chart--text-s')
      .attr('y', (d) => `${this.chartVars.fy(d.key) + 20}`)
      .attr('fill', 'black');

    const legend = d3
      .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
      .selectAll('.chart--legend-item');

    // Detect if chart is in view based on passed config and if so start animation
    this.isElementInViewOnce(this.ui.d3Selection, () => {
      this.chartShown = true;
      // Animate bar width
      bars
        .attr('data-bar-rect', '') // "data-bar-rect" attribute helps selectors know this is a bar
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          // Animation stagger is determined by looking up the this.animationSettings.animationIndexProperty on the data. For example, in a horizontal bar chart the leftmost bar is has an index of 0, following that 1, 2, etc.
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr('width', (d) => this.chartVars.x(this.parseNumber(d[this.chartVars.xProp])));

      // Animate chart percentage labels
      percentages
        .attr('data-bar-percent', '') // "data-bar-percent" attribute helps selectors know this is a percent
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        } )
        .attr('x', (d) => this.chartVars.x(this.parseNumber(d[this.chartVars.xProp])) + 8);

      barTitles
        .attr('data-bar-title', '')
        .attr('data-bar-index', (d) => d[this.animationSettings.animationIndexProperty]);

      // Attach hover animations on mouseover/mouseout on both the bars and the percentage labels (hovering over a label also shows an animation)
      [bars, percentages, legend].forEach((element) => element.on('mouseover', function(event) {
        const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

        // Animate bars on hover (selects all non-hovered bars)
        d3
          .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
          .selectAll(allBarsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteOne(d[thisScope.chartVars.yProp]));

        const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

        // Animate percent labels on hover (selects all non-hovered percent labels)
        d3
          .select(thisScope.element)
          .selectAll(allBarPercentagesExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => thisScope.chartVars.palettes.highlightPaletteTwo(d[thisScope.chartVars.yProp]),
          );
      })
        .on('mouseout', function(event) {
          // Animate bars on mouseout (selects all non-hovered bars)
          const allBarsExceptHoveredOne = `[data-bar-rect][data-bar-index]:not([data-bar-rect][data-bar-index="${event.target.dataset.barIndex}"])`;
          d3
            .select(thisScope.element)
            .selectAll(allBarsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d[thisScope.chartVars.yProp]));

          const allBarPercentagesExceptHoveredOne = `[data-bar-percent][data-bar-index]:not([data-bar-percent][data-bar-index="${event.target.getAttribute('data-bar-index')}"])`;

          // Animate chart percentage labels
          percentages
            .attr('data-bar-percent', '') // "data-bar-percent" attribute helps selectors know this is a percent
            .attr(
              'data-bar-index',
              (d) => d[thisScope.animationSettings.animationIndexProperty],
            ) // "data-bar-index" provides grouping of different bar-related elements (ex: text, rect) to make selecting elements easiers
            .transition(thisScope.animationSettings.animationGrowTransitionName)
            .duration(thisScope.animationSettings.animationDuration)
            .delay((d) => {
              return (
                thisScope.animationSettings.animationStartDelay +
              d[thisScope.animationSettings.animationIndexProperty] *
                thisScope.animationSettings.animationStagger
              );
            });

          // Animate percent labels on mouseout (selects all non-hovered percent labels)
          d3
            .select(thisScope.element)
            .selectAll(allBarPercentagesExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteTwo(d[thisScope.chartVars.yProp]));
        }));
    }, this.animationSettings.animationInViewThreshold);
  }

  buildLikertBarChartHorizontalBars() { // lb2
    const thisScope = this;
    this.isElementInViewOnce(this.ui.d3Selection, () => {
      // stacked bars
      const bars = this.svg
        .append('g')
        .selectAll()
        .data(this.series)
        .join('g')
        .attr('transform', (d) => {
          return `translate(0,${this.chartVars.fy(d.key) - 15})`;
        })
        .selectAll()
        .data((d) => d)
        .join('g')
        .attr(
          'transform',
          (d, i) => {
            return `translate(0,${this.chartVars.y(d.key) - (i * 10)})`;
          },
        )
        .selectAll()
        .data((d) => d)
        .join('rect')
        .attr('x', 0)
        .attr('y', 50)
        .attr('height', 21)
        .attr(
          'width', 0,
        );

      // group titles
      this.svg
        .append('g')
        .selectAll()
        .data(this.series)
        .join('text')
        .text((d) => `${d.key}`)
        .attr('height', 20)
        .attr('class', 'chart--text-s')
        .attr('transform', (d) => {
          return `translate(0,${this.chartVars.fy(d.key) + 15})`;
        })
        .attr('width', () => {
          return 50;
        });

      // stacked bar titles
      this.svg
        .append('g')
        .selectAll()
        .data(this.series)
        .join('g')
        .attr('transform', (d) => {
          return `translate(0,${this.chartVars.fy(d.key) + 30})`;
        })
        .selectAll()
        .data((d) => {
          return d;
        })
        .join('text')
        .text((d) => `${d.key}`)
        .attr('height', 18)
        .attr('class', 'chart--text-xs')
        .attr('transform', (d, i) => {
          return `translate(0,${this.chartVars.y(d.key) - (i * 10)})`;
        })
        .attr('width', () => {
          return 50;
        });

      // percentages
      const percentages = this.svg
        .append('g')
        .selectAll()
        .data(this.series)
        .join('g')
        .attr('transform', (d) => {
          return `translate(0,${this.chartVars.fy(d.key) - 15})`;
        })
        .selectAll()
        .data((d) => d)
        .join('g')
        .attr(
          'transform',
          (d, i) => `translate(0,${this.chartVars.y(d.key) - (i * 10)})`,
        )
        .selectAll()
        .data((d) => d)
        .join('text')
        .filter((d) => d[1] !== d[0])
        .text((d) => {
          return `${d[1] - d[0]}%`;
        })
        .attr('x', 0)
        .attr('y', 50)
        .attr('class', 'chart--text-xs')
        .attr('height', 21)
        .attr('width', (d) => this.chartVars.x((d[1] - d[0]) / d['total']))
        .attr('fill', (d) => {
          if (
            // Text needs to be black if outside of the bar (on a white background)
            d.x >= (100 - this.chartVars.poppedPercent) ||
            (d.x === 0 && d[1] - d[0] <= this.chartVars.poppedPercent && this.matchMediaMobile.matches)
          ) {
            return '#000000';
          } else {
            return this.chartVars.palettes.paletteTwo(d.data[0]);
          }
        })
        .attr('text-anchor', (d) => {
          if (d.x === 0 && d[1] - d[0] <= this.chartVars.poppedPercent && this.matchMediaMobile.matches) {
            return 'end';
          }
          return 'left';
        })
        .attr('transform', (d) => {
          if (d.x >= (100 - this.chartVars.poppedPercent)) {
            return `translate(${
              6 +
              this.chartVars.x((d['total'] - d.x) / d['total']) -
              this.chartVars.margins.left
            },15)`;
          } else if (
            d.x === 0 &&
            d[1] - d[0] <= this.chartVars.poppedPercent &&
            this.matchMediaMobile.matches
          ) {
            return `translate(${-6},15)`;
          } else if (
            d.x === 0 &&
            d[1] - d[0] <= this.chartVars.poppedPercent
          ) {
            return `translate(${3},15)`;
          } else {
            return 'translate(6,15)';
          }
        });

      // Likert bar animations

      // Animate bar width
      bars
        .attr('data-bar-rect', '')
        .attr('data-likert-bar-chart-horizontal-bars-category', (d) => {
          return d.data[0];
        })
        .attr('fill', (d) => this.chartVars.palettes.paletteOne(d.data[0]))
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.animationDuration)
        .delay((d) => {
          return this.animationSettings.animationStartDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        })
        .attr(
          'width',
          (d) =>
            this.chartVars.x((d[1] - d[0]) / d['total']) -
          this.chartVars.margins.left,
        )
        .attr('x', (d) => this.chartVars.x(d['x'] / d['total']));

      // Animate percentage opacity from 0 to 1
      percentages
        .attr('data-bar-percent', '')
        .attr('data-likert-bar-chart-horizontal-bars-category', (d) => {
          return d.data[0];
        })
        .attr('opacity', 0)
        .transition(this.animationSettings.animationGrowTransitionName)
        .duration(this.animationSettings.likertBarChartHorizontalBarsPercentageAnimationDuration)
        .delay((d) => {
          return this.animationSettings.likertBarChartHorizontalBarsPercentageAnimationDelay + d[this.animationSettings.animationIndexProperty] * this.animationSettings.animationStagger;
        })
        .attr('opacity', 1)
        .attr('x', (d) => this.chartVars.x(d['x'] / d['total']));
      const legend = d3
        .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
        .selectAll('.chart--legend-item');

      [bars, percentages, legend].forEach((element) => element.on('mouseover', function(event) {
        const allLikertCategoriesBarsExceptHoveredOne = `[data-bar-rect][data-likert-bar-chart-horizontal-bars-category]:not([data-bar-rect][data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;

        // // Animate bars on hover (selects all non-hovered bars)
        d3
          .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
          .selectAll(allLikertCategoriesBarsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => {
            return thisScope.chartVars.palettes.highlightPaletteOne(d.data[0]);
          });

        const allLikertCategoriesPercentsExceptHoveredOne = `[data-bar-percent][data-likert-bar-chart-horizontal-bars-category]:not([data-bar-percent][data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;
        // Animate percent labels on hover (selects all non-hovered percent labels)
        d3
          .select(thisScope.element)
          .selectAll(allLikertCategoriesPercentsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .attr('fill', (d) => {
            if (
              d.x >= (100 - thisScope.chartVars.poppedPercent) ||
            (d.x === 0 && d[1] - d[0] <= thisScope.chartVars.poppedPercent && thisScope.matchMediaMobile.matches)
            ) {
              return thisScope.config.chartLegendTextHighlightColors[d._index];
            } else {
              return thisScope.chartVars.palettes.highlightPaletteTwo(d.data[0]);
            }
          });

        // select all legend color squares and apply hover state
        const allLikertCategoriesLegendColorsExceptHoveredOne = `.chart--legend-item-color[data-likert-bar-chart-horizontal-bars-category]:not(.chart--legend-item-color[data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;

        d3
          .select(thisScope.element)
          .selectAll(allLikertCategoriesLegendColorsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .style('background-color', (d, index, groups) => {
            return groups[index].getAttribute('data-legend-highlight-color');
          });

        // select all legend text labels and apply hover state
        const allLikertCategoriesLegendLabelsExceptHoveredOne = `.chart--legend-item-label[data-likert-bar-chart-horizontal-bars-category]:not(.chart--legend-item-label[data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;

        d3
          .select(thisScope.element)
          .selectAll(allLikertCategoriesLegendLabelsExceptHoveredOne)
          .transition(thisScope.animationSettings.animationHoverTransitionName)
          .duration(thisScope.animationSettings.animationHoverDuration)
          .style('color', (d, index, groups) => {
            return groups[index].getAttribute('data-legend-text-highlight-color');
          });
      })
        .on('mouseout', function(event) {
          const allLikertCategoriesBarsExceptHoveredOne = `[data-bar-rect][data-likert-bar-chart-horizontal-bars-category]:not([data-bar-rect][data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;

          // // Animate bars on hover (selects all non-hovered bars)
          d3
            .select(thisScope.element) // limit selector to this element to prevent affecting other charts on the page
            .selectAll(allLikertCategoriesBarsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName) // names on all transitions are provided to prevent conflicting animation issues
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => thisScope.chartVars.palettes.paletteOne(d.data[0]));

          const allLikertCategoriesPercentsExceptHoveredOne = `[data-bar-percent][data-likert-bar-chart-horizontal-bars-category]:not([data-bar-percent][data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;
          // Animate percent labels on hover (selects all non-hovered percent labels)
          d3
            .select(thisScope.element)
            .selectAll(allLikertCategoriesPercentsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .attr('fill', (d) => {
              if (
                d.x >= (100 - thisScope.chartVars.poppedPercent) ||
                (d.x === 0 && d[1] - d[0] <= thisScope.chartVars.poppedPercent && thisScope.matchMediaMobile.matches)
              ) {
                return '#000000';
              } else {
                return thisScope.chartVars.palettes.paletteTwo(d.data[0]);
              }
            });

          // select all legend color squares and remove hover state
          const allLikertCategoriesLegendColorsExceptHoveredOne = `.chart--legend-item-color[data-likert-bar-chart-horizontal-bars-category]:not(.chart--legend-item-color[data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;

          d3
            .select(thisScope.element)
            .selectAll(allLikertCategoriesLegendColorsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .style('background-color', (d, index, groups) => {
              return groups[index].getAttribute('data-legend-color');
            });

          // select all legend text labels and apply hover state
          const allLikertCategoriesLegendLabelsExceptHoveredOne = `.chart--legend-item-label[data-likert-bar-chart-horizontal-bars-category]:not(.chart--legend-item-label[data-likert-bar-chart-horizontal-bars-category="${event.target.getAttribute('data-likert-bar-chart-horizontal-bars-category')}"]`;

          d3
            .select(thisScope.element)
            .selectAll(allLikertCategoriesLegendLabelsExceptHoveredOne)
            .transition(thisScope.animationSettings.animationHoverTransitionName)
            .duration(thisScope.animationSettings.animationHoverDuration)
            .style('color', (d, index, groups) => {
              return groups[index].getAttribute('data-legend-text-color');
            });
        }));
    }, this.animationSettings.animationInViewThreshold);
  }

  getPalettes(set, fallbackColor = '#ccc') {
    const paletteOne = d3
      .scaleOrdinal()
      .domain(set)
      .range(this.config.chartPalette)
      .unknown(fallbackColor);
    const paletteTwo = d3
      .scaleOrdinal()
      .domain(set)
      .range(this.config.chartPaletteSecondary)
      .unknown(fallbackColor);
    const highlightPaletteOne = d3
      .scaleOrdinal()
      .domain(set)
      .range(this.config.chartPaletteBackgroundHighlights)
      .unknown(fallbackColor);
    const highlightPaletteTwo = d3
      .scaleOrdinal()
      .domain(set)
      .range(this.config.chartPaletteTextHighlights)
      .unknown(fallbackColor);
    return {paletteOne, paletteTwo, highlightPaletteOne, highlightPaletteTwo};
  }

  getStyleMap(data, prop) {
    const styleMap = {};
    data.forEach((d) => {
      const value = d[prop];
      const parsedNumber = this.parseNumber(value);
      styleMap[parsedNumber] = value;
    });
    return styleMap;
  }

  determineAxisType(entry, property) {
    return entry[property].match(/\d\d\d\d-\d\d-\d\d/) ? 'date' : 'number';
  }

  parseNumber(string) {
    if (typeof string !== 'string') return string;
    return +string.replace(/[^0-9.]/g, '');
  }

  parseDate(string) {
    try {
      return new Date(string);
    } catch (e) {
      return null;
    }
  }

  getSankeyPadding(rows) {
    let padding = 10; // padding below
    if (rows > 0) {
      padding += (rows * 12) + 4;
    }
    return padding;
  }

  getDonutCustomAxis(data) {
    const set = new Set(data.map((datum) => datum[this.chartVars.xProp]));
    const customAxis = [...set].map((label, i) => {
      const splitToken = label.includes('<br>') ? '<br>' : ' ';
      return label.split(splitToken).map((word) => {
        return {text: word, index: i, dupe: !!data[i].dupe};
      });
    });
    return customAxis;
  }
}
registerModule('Chart', Chart);
